import { useState, useEffect, useCallback, useMemo } from "react";
import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import {
  uid, fmtS, fmtF, fmtDateRange, isoToday, isoMake, nightCount, overlap,
  cleanPhone, getRate, bcomCalc,
  fmtGuests, bookingFromDb, bookingToDb, roomFromDb, roomToDb, buildMultiBookingLabels,
  curSym,
} from './utils';
import { DEFAULT_TEMPLATES } from './templates.js';
import { localiseError } from './errors.js';
import { CIRCLE_KEYS, countTodayActions } from './comm-tracker.js';
import { BASELINE_TYPES_EN, BASELINE_TYPES_BG, resolveTypeLabel, getKnownTypeKeys } from './room-types.js';
import { CL, FONT, HAIRLINE, st, badge, payCol } from './theme.js';
import { Ic, Diamond, IcWA, IcViber, IcMail, HamburgerIcon } from './icons.jsx';
import { CHANNEL_STYLES } from './channels.js';
import { extraLabel, displaySource, isSofaBedExtra, isCotExtra, isOccasionExtra } from './labels.js';
import { SectionHeader, ConfirmModal, DeleteRoomModal, DepositAmountModal } from './ui.jsx';
import { TodayActionsFeed, CommPanel, PaymentQuickPick } from './comms.jsx';
import BkForm from './BkForm.jsx';
import Analytics from './Analytics.jsx';
import { AddRoomModal, CustomTypeForm } from './AddRoomModal.jsx';
import { SettingsPanel } from './settings.jsx';
import OnboardingWizard from './Onboarding.jsx';
import { wizardStepFromProperty } from './onboarding-utils.js';

/* ═══ WELCOME BANNER ═══ */
const WELCOME_DISMISS_KEY = 'tavelo_welcome_dismissed';
const PDF_LINK_PLACEHOLDER = 'https://tavelo.app/#contact';

/* ═══ CONSTANTS ═══ */
// Fallbacks used only when a property has no `sources` configured (legacy rows).
const SRC_BG = ["Booking.com","Телефон","Facebook","Instagram","Директно","Друго"];
const SRC_EN = ["Booking.com","Phone","Facebook","Instagram","Direct","Other"];
const PAY_OPTS = ["Unpaid","Deposit","Paid"];
const M_BG = ["Яну","Фев","Мар","Апр","Май","Юни","Юли","Авг","Сеп","Окт","Ное","Дек"];
const M_EN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const D_BG = ["Пн","Вт","Ср","Чт","Пт","Сб","Нд"];
const D_EN = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const LANG = {
  bg: {
    hotel:"", sub:"",
    today:"Днес", occ:"Заетост", dir:"Гости", rooms:"Стаи",
    newBk:"Нова Резервация", editBk:"Промяна", save:"Запази", upd:"Обнови",
    cancel:"Отказ", edit:"Промени", del:"Изтрий",
    delConf:"Изтриване завинаги?", yes:"Да", no:"Не",
    name:"Име на гост", phone:"Телефон", phoneSub:"търси познат гост",
    phoneReq:"Телефонът е задължителен",
    email:"Имейл", opt:"по избор",
    ci:"Настаняване", co:"Напускане",
    room:"Стая", avail:"свободни",
    priceN:"На нощ", priceT:"Общо",
    payment:"Плащане", source:"Източник",
    adults:"Възрастни", children:"Деца", notes:"Бележки",
    nts:"нощувки", nt:"нощувка", nShort:"н",
    stays:"престоя", stay:"престой",
    occupied:"Заети", free:"Свободни", revenue:"Приходи",
    roomOccupied:"● Заета", roomFree:"Свободна",
    ciToday:"Настанявания днес", coToday:"Напускания днес",
    inHouse:"В хотела", none:"Няма",
    retGuests:"Завръщащи се гости", totalDir:"общо в регистъра",
    guestDir:"Регистър", guests:"гости",
    returning:"Завръщащи се", allG:"Всички гости",
    searchPh:"Търси по име или телефон...", noMatch:"Няма съвпадения",
    noGYet:"Добавете резервации с телефон",
    profile:"Профил на гост", spent:"Похарчени", favRm:"Любими стаи",
    hist:"История", booking:"Резервация",
    tapProf:"Натисни за профил →", retGuest:"Познат гост!",
    matchG:"Намерени гости",
    booked:"Резервирани", gaps:"Свободни", rate:"Процент",
    gapOpp:"Свободни периоди", fullBk:"Напълно заето!",
    potential:"потенциал", total:"Общо",
    tapEdit:"Натисни за промяна", upTo:"до",
    saved:"Запазено!", updated:"Обновено!", deleted:"Изтрито",
    enterNm:"Въведи име на гост", selRm:"Избери стая", setDt:"Задай дати",
    coAfter:"Датата на напускане трябва да е след настаняването",
    rmNA:"Стаята е заета!",
    paid:"Платено", unpaid:"Неплатено", deposit:"Депозит",
    moveRm:"Премести", moveTitle:"Преместване в друга стая",
    freeRms:"Свободни стаи", occRms:"Заети стаи (Размяна)",
    swapW:"Размяна с", swapConf:"Потвърди размяна",
    swapOk:"Стаите са разменени!", moveOk:"Стаята е преместена!",
    cantSwap:"Не може, конфликт на дати", noRmMove:"Няма други свободни стаи",
    refRate:"Референтна цена", gross:"Брутно", net:"Нето за хотела",
    commission:"Комисиона", vccFee:"VCC/банкова такса",
    bcomNote:"Разходи Booking.com",
    selectDates:"Натисни дата за настаняване, после за напускане",
    seasonRates:"Сезонни цени", addSeason:"+ Добави период", minNights:"мин. нощи",
    perNight:"нощ", months: M_BG, days: D_BG,
    bulkRoom:"Стаи", bulkApt:"Апартаменти",
    bulkApply:"Приложи за всички", bulkApplied:"Запазено!",
    roomLabel:"Стая", aptLabel:"Апартамент",
    maxGuests:"макс. гости",
    viewMonth:"Месец", viewWeek:"Седмица",
    guestNote:"Бележки за гост", guestNotePh:"напр. предпочита долу, иска тераса...",
    guestNoteSaved:"Бележка запазена!",
    notesCi:"Бележки за настаняване", notesCo:"Бележки за напускане",
    notesCiPh:"напр. късно пристигане, алергия към прах...",
    notesCoPh:"напр. късно напускане, трансфер до летище...",
    pastDateWarn:"Минала дата. Провери дали е правилно",
    adult:"Възрастен", child:"Дете",
    depositAmt:"Сума на депозита", depositPaid:"Платен депозит", balanceDue:"Остатък за плащане", depositAmtReq:"Въведи сума на депозита",
    childAge:"Години",
    extras:"Допълнителни",
    breakfast: 'Закуска',
    breakfastPerPersonMath: (cur, price, count, nights, total) =>
      `${cur}${price} на човек/нощ × ${count} с право × ${nights} нощувки = ${cur}${total}`,
    breakfastPerRoomMath: (cur, price, nights, total) =>
      `${cur}${price}/нощ × ${nights} нощувки = ${cur}${total}`,
    breakfastEligibilityHint: (adults, ageBoundary) =>
      ageBoundary != null
        ? `(с право = ${adults} възрастни + деца на възраст ≥ ${ageBoundary})`
        : `(с право = ${adults} възрастни, деца безплатно)`,
    breakfastIncludedStatic: 'Закуска: включена',
    breakfastLockedSnapshot: (cur, price) =>
      `Закуска (${cur}${price}), фиксирана при резервацията`,
    breakfastLockedTotal: (cur, total) =>
      `${cur}${total} (фиксирана при резервацията)`,
    breakfastLockedHint: (cur, unitPrice, mode, liveTotal) =>
      mode === 'per_person'
        ? `Текущата цена е ${cur}${unitPrice}/човек = ${cur}${liveTotal}. Изключи и включи отново за обновяване`
        : `Текущата цена е ${cur}${unitPrice}/нощ = ${cur}${liveTotal}. Изключи и включи отново за обновяване`,
    breakfastNegPrice: 'Цената на закуската не може да е отрицателна',
    extraBeds: 'Допълнителни легла',
    extraBedCostLabel: 'Допълнително легло',
    extraBedNight: 'Доп. легло/нощ',
    baseRate: 'Основна цена',
    priceNRoom: 'Нощ (стая)',
    analytics: {
      tabLabel:          'Табло',
      periods: {
        thisSeason:      'Този сезон',
        lastSeason:      'Миналия сезон',
        thisMonth:       'Този месец',
        last30:          'Последни 30 дни',
        ytd:             'От началото на годината',
        custom:          'По избор…',
      },
      compare: {
        lastYear:        'Сравни с миналата година',
        notAvailable:    'Сравнението ще се появи следващия сезон',
      },
      kpi: {
        revenue:         'Приходи (нето)',
        occupancy:       'Заетост',
        adr:             'Средна нощ. цена',
        adrTip:          'Средна нощувна цена на резервирана нощ. Нето = след приспадане на комисионна. Брутно = преди комисионна.',
        otaCost:         'Разход Комисионни',
        revpar:          'Доход/стая',
        revparTip:       'Приход на налична стая. Пример: 80% заетост × €150 нощувка = €120 доход/стая.',
        net:             'Нето',
        gross:           'Брутно',
        includesFuture:  'включва бъдещи резервации',
      },
      pace: {
        title:           'Заетост по месеци',
        booked:          '{pct}% заети към днес',
        annotation:      'Сравнението ще се появи следващия сезон',
      },
      extras: {
        title:              'Допълнителни приходи',
        breakfast:          'Закуска',
        extraBeds:          'Допълнителни легла',
        ofRevenue:          'от нетните приходи',
        ofNights:           'от нощувките',
        revenueLabel:       'Приход',
        pctOfRevenue:       '{pct}% от нетните приходи идват от допълнения',
        extraBedPenetration:'Нощ. с допълнително легло по тип стая:',
        bfPenetration:      'Закуска включена в {pct}% от нощувките',
      },
      weekdayWeekend: {
        title:   'Заетост: Пик vs. Извън пик',
        peak:    'Пт + Сб (пик)',
        offPeak: 'Ост. дни (извън пик)',
        note:    'Пиковите нощи са петък и събота.',
      },
      los: {
        title:        'Продължителност на престоя',
        mean:         'Средно: {n} нощувки',
        perMonth:     'Нощи по месец',
        nightsUnit:   'нощ.',
        nightShort:   'н',
        reservations: 'резервации',
        avgStay:      'Средно пребиваване',
        totalSummary: '{count} резервации · средно {avg} нощ.',
      },
      leadTime: {
        title:    'Дни предварително',
        note:     'Средният брой дни между въвеждане на резервацията и пристигането.',
        avgLabel: 'Дни предварително',
        dayShort: 'д',
        daysLabel: 'дни',
        mean:     'Средно за периода: резервирано {n} дни предварително',
      },
      breakdown: {
        perRoomType:     'По вид стая',
        perRoom:         'По стая',
        allRooms:        'Всички',
      },
      revenueBySource:   'Приходи по канал',
      sources:           { Direct: 'Директно', Phone: 'Телефон' },
      other:             'Други',
      empty:             'Няма резервации за този период',
      historicalNote:    'Промените в процента важат само за нови резервации. Старите запазват стойността от момента на въвеждане.',
      season: {
        label:           'Сезон',
        edit:            'Редактиране',
        editTitle:       'Редактиране на сезон',
        helpText:        'Посочете началото и края на вашия сезон. Използвайте 1 ян – 31 дек, ако работите целогодишно.',
        start:           'Начало',
        end:             'Край',
        save:            'Запази',
        cancel:          'Отказ',
        invalid:         'Невалидна дата',
      },
      multiYear: {
        toggleSeason:    'Сезонен изглед',
        toggleMultiYear: 'Многогодишен изглед',
        yearlyRevenue:   'Приходи по година',
        monthYearGrid:   'Месечни приходи спрямо предходна година',
        hideYear:        'Скрий {year}',
        showYear:        'Покажи {year}',
        allYearsHidden:  'Всички години са скрити',
        sourceMixPerYear:'Канали по година',
        addEarlier:      '+ Добави по-стара година',
        addYearData:     '+ Добави данни за {year}',
        sourceUnknown:   'каналите не са попълнени',
        legendBackfill:  '● = ръчно въведено',
        legendReal:      '■ = от резервации',
        kpiRevenue:      'Приходи',
        kpiNights:       'Нощувки',
        kpiAdr:          'Средна цена',
        warningCollision:'Имате реални резервации за {year}. Моля, преминете към месечно въвеждане, за да избегнете двойно отчитане.',
      },
      backfill: {
        title:           'Добави данни за {year}',
        editTitle:       'Редактиране на {year}',
        revenueLabel:    'Общи приходи (€)',
        revenueRequired: 'Задължително',
        nightsLabel:     'Общо нощувки (по избор)',
        nightsHelp:      'Нужно за изчисление на средна цена',
        toggleMonthly:   '▸ Добави разбивка по месец',
        toggleSource:    '▸ Добави разпределение по канал',
        monthlyHeader:   'Приходи по месец',
        monthRevenue:    'Приходи (€)',
        monthNights:     'Нощувки',
        sourceDirect:    'Директно',
        sourceBcom:      'Booking.com',
        sourceOther:     'Други',
        sourceSumError:  'Сборът от каналите ({sum} €) не съвпада с общите приходи ({total} €).',
        revenueRequiredError: 'Моля, въведете общите приходи.',
        overlapBanner:   'Имаме записани резервации за месеците {months} {year}. Попълнете останалите месеци, ако имате записки.',
        overlapMonthLocked: 'от резервации',
        save:            'Запази',
        cancel:          'Отказ',
        remove:          'Премахни',
        removeConfirm:   'Да премахна ли всички ръчно въведени данни за {year}?',
      },
    },
  },
  en: {
    hotel:"", sub:"",
    today:"Today", occ:"Occupancy", dir:"Directory", rooms:"Rooms",
    newBk:"New Booking", editBk:"Edit Booking", save:"Save Booking", upd:"Update",
    cancel:"Cancel", edit:"Edit", del:"Delete",
    delConf:"Delete permanently?", yes:"Yes", no:"No",
    name:"Guest Name", phone:"Phone", phoneSub:"find returning guest",
    phoneReq:"Phone number is required",
    email:"Email", opt:"optional",
    ci:"Check-in", co:"Check-out",
    room:"Room", avail:"available",
    priceN:"Per Night", priceT:"Total",
    payment:"Payment", source:"Source",
    adults:"Adults", children:"Children", notes:"Notes",
    nts:"nights", nt:"night", nShort:"n",
    stays:"stays", stay:"stay",
    occupied:"Occupied", free:"Free", revenue:"Revenue",
    roomOccupied:"● Occupied", roomFree:"Free",
    ciToday:"Check-ins Today", coToday:"Check-outs Today",
    inHouse:"In-House", none:"None",
    retGuests:"Returning Guests", totalDir:"total in directory",
    guestDir:"Guest Directory", guests:"guests",
    returning:"Returning", allG:"All Guests",
    searchPh:"Search name or phone...", noMatch:"No matches",
    noGYet:"Add bookings with phone numbers",
    profile:"Guest Profile", spent:"Spent", favRm:"Favourite Rooms",
    hist:"Stay History", booking:"Booking",
    tapProf:"Tap for full profile →", retGuest:"Returning Guest!",
    matchG:"Matching Guests",
    booked:"Booked", gaps:"Gaps", rate:"Rate",
    gapOpp:"Gap Opportunities", fullBk:"Fully booked!",
    potential:"potential", total:"Total",
    tapEdit:"Tap to edit", upTo:"up to",
    saved:"Saved!", updated:"Updated!", deleted:"Deleted",
    enterNm:"Enter guest name", selRm:"Select a room", setDt:"Set dates",
    coAfter:"Check-out must be after check-in",
    rmNA:"Room not available!",
    paid:"Paid", unpaid:"Unpaid", deposit:"Deposit",
    moveRm:"Move Room", moveTitle:"Move to Another Room",
    freeRms:"Free Rooms", occRms:"Occupied (Swap)",
    swapW:"Swap with", swapConf:"Confirm Swap",
    swapOk:"Rooms swapped!", moveOk:"Room moved!",
    cantSwap:"Cannot swap — dates conflict", noRmMove:"No rooms available",
    refRate:"Reference rate", gross:"Gross", net:"Net to hotel",
    commission:"Commission", vccFee:"VCC/payment fee",
    bcomNote:"Booking.com costs",
    selectDates:"Tap check-in date, then check-out",
    seasonRates:"Seasonal Rates", addSeason:"+ Add Period", minNights:"min nights",
    perNight:"night", months: M_EN, days: D_EN,
    bulkRoom:"Rooms", bulkApt:"Apartments",
    bulkApply:"Apply to all", bulkApplied:"Saved!",
    roomLabel:"Room", aptLabel:"Apartment",
    maxGuests:"max guests",
    viewMonth:"Month", viewWeek:"Week",
    guestNote:"Guest Notes", guestNotePh:"e.g. prefers ground floor, wants terrace...",
    guestNoteSaved:"Note saved!",
    notesCi:"Check-in Notes", notesCo:"Check-out Notes",
    notesCiPh:"e.g. late arrival, allergy to dust...",
    notesCoPh:"e.g. late check-out, airport transfer...",
    pastDateWarn:"Past date — double-check this is correct",
    adult:"Adult", child:"Child",
    depositAmt:"Deposit Amount", depositPaid:"Deposit paid", balanceDue:"Balance due", depositAmtReq:"Enter deposit amount",
    childAge:"Age (yrs)",
    extras:"Extras",
    breakfast: 'Breakfast',
    breakfastPerPersonMath: (cur, price, count, nights, total) =>
      `${cur}${price} per person/night × ${count} eligible × ${nights} nights = ${cur}${total}`,
    breakfastPerRoomMath: (cur, price, nights, total) =>
      `${cur}${price}/night × ${nights} nights = ${cur}${total}`,
    breakfastEligibilityHint: (adults, ageBoundary) =>
      ageBoundary != null
        ? `(eligible = ${adults} adults + children age ≥ ${ageBoundary})`
        : `(eligible = ${adults} adults — kids free)`,
    breakfastIncludedStatic: 'Breakfast: included',
    breakfastLockedSnapshot: (cur, price) =>
      `Breakfast (${cur}${price}) — locked at original booking`,
    breakfastLockedTotal: (cur, total) =>
      `${cur}${total} (locked at booking)`,
    breakfastLockedHint: (cur, unitPrice, mode, liveTotal) =>
      mode === 'per_person'
        ? `Property rate is now ${cur}${unitPrice}/person = ${cur}${liveTotal} — toggle off+on to update`
        : `Property rate is now ${cur}${unitPrice}/night = ${cur}${liveTotal} — toggle off+on to update`,
    breakfastNegPrice: 'Breakfast price cannot be negative',
    extraBeds: 'Extra beds',
    extraBedCostLabel: 'Extra bed',
    extraBedNight: 'Extra bed/night',
    baseRate: 'Base rate',
    priceNRoom: 'Room/night',
    analytics: {
      tabLabel:          'Dashboard',
      periods: {
        thisSeason:      'This season',
        lastSeason:      'Last season',
        thisMonth:       'This month',
        last30:          'Last 30 days',
        ytd:             'Year to date',
        custom:          'Custom…',
      },
      compare: {
        lastYear:        'Compare to last year',
        notAvailable:    'Comparison will light up next season',
      },
      kpi: {
        revenue:         'Revenue (net)',
        occupancy:       'Occupancy',
        adr:             'ADR',
        adrTip:          'Average daily rate — revenue per booked night. Net = after commissions. Gross = before commissions.',
        otaCost:         'OTA cost',
        revpar:          'RevPAR',
        revparTip:       'Revenue Per Available Room. E.g. 80% occupancy × €150 rate = €120 RevPAR.',
        net:             'Net',
        gross:           'Gross',
        includesFuture:  'includes bookings still to arrive',
      },
      pace: {
        title:           'Occupancy by month',
        booked:          '{pct}% booked as of today',
        annotation:      'Comparison will light up next season',
      },
      extras: {
        title:              'Extras breakdown',
        breakfast:          'Breakfast',
        extraBeds:          'Extra beds',
        ofRevenue:          'of net revenue',
        ofNights:           'of stays',
        revenueLabel:       'Revenue',
        pctOfRevenue:       '{pct}% of net revenue comes from extras',
        extraBedPenetration:'Stays with extra bed by room type:',
        bfPenetration:      'Breakfast included in {pct}% of stays',
      },
      weekdayWeekend: {
        title:   'Occupancy — Peak vs. Off-peak',
        peak:    'Fri + Sat (peak)',
        offPeak: 'Other days (off-peak)',
        note:    'Peak nights are Friday and Saturday.',
      },
      los: {
        title:        'Length of stay',
        mean:         'Average: {n} nights',
        perMonth:     'Stay length by month',
        nightsUnit:   'nights',
        nightShort:   'n',
        reservations: 'bookings',
        avgStay:      'Avg stay',
        totalSummary: '{count} bookings · avg {avg} nights',
      },
      leadTime: {
        title:    'Days booked in advance',
        note:     'Average days between a booking being recorded and the guest arriving.',
        avgLabel: 'Days in advance',
        dayShort: 'd',
        daysLabel: 'days',
        mean:     'Period average: booked {n} days in advance',
      },
      breakdown: {
        perRoomType:     'Per room type',
        perRoom:         'Per room',
        allRooms:        'All',
      },
      revenueBySource:   'Revenue by source',
      sources:           { Direct: 'Direct', Phone: 'Phone' },
      other:             'Other',
      empty:             'No bookings in this period',
      historicalNote:    'Changes apply to new bookings only. Existing bookings keep the rate stamped when they were created.',
      season: {
        label:           'Season',
        edit:            'Edit',
        editTitle:       'Edit season',
        helpText:        "Set the start and end of your booking season. Use Jan 1 – Dec 31 if you're open year-round.",
        start:           'Start',
        end:             'End',
        save:            'Save',
        cancel:          'Cancel',
        invalid:         'Invalid date',
      },
      multiYear: {
        toggleSeason:    'Season view',
        toggleMultiYear: 'Multi-year view',
        yearlyRevenue:   'Revenue per year',
        monthYearGrid:   'Monthly revenue, year over year',
        hideYear:        'Hide {year}',
        showYear:        'Show {year}',
        allYearsHidden:  'All years hidden',
        sourceMixPerYear:'Source mix per year',
        addEarlier:      '+ Add earlier year',
        addYearData:     '+ Add {year} data',
        sourceUnknown:   'source mix unknown',
        legendBackfill:  '● = manually entered',
        legendReal:      '■ = from bookings',
        kpiRevenue:      'Revenue',
        kpiNights:       'Nights',
        kpiAdr:          'ADR',
        warningCollision:'You have real bookings for {year}. Please switch to monthly entry to avoid double-counting.',
      },
      backfill: {
        title:           'Add data for {year}',
        editTitle:       'Edit {year}',
        revenueLabel:    'Total revenue (€)',
        revenueRequired: 'Required',
        nightsLabel:     'Total nights (optional)',
        nightsHelp:      'Needed for average-rate calculation',
        toggleMonthly:   '▸ Add monthly breakdown',
        toggleSource:    '▸ Add source split',
        monthlyHeader:   'Revenue by month',
        monthRevenue:    'Revenue (€)',
        monthNights:     'Nights',
        sourceDirect:    'Direct',
        sourceBcom:      'Booking.com',
        sourceOther:     'Other',
        sourceSumError:  'Source split ({sum} €) does not match total revenue ({total} €).',
        revenueRequiredError: 'Please enter the total revenue.',
        overlapBanner:   'We have bookings recorded for {months} {year}. Add the remaining months below if you have notes for them.',
        overlapMonthLocked: 'from bookings',
        save:            'Save',
        cancel:          'Cancel',
        remove:          'Remove',
        removeConfirm:   'Remove all manually entered data for {year}?',
      },
    },
  },
};

/* ═══ UTILS (imported from ./utils) ═══ */

/* ═══ WELCOME BANNER ═══ */
function WelcomeBanner({ lang, onDismiss }) {
  const L = lang === 'bg' ? {
    title: 'Настройката е готова. Добре дошли в Tavelo.',
    body: 'Пишете на ',
    bodyMid: ', за да насрочим 30-минутно обаждане за настройка, или следвайте ',
    guide: 'Ръководството за настройка',
    bodyTail: ', за да продължите сами.',
    dismiss: 'затвори',
  } : {
    title: 'Setup complete — welcome to Tavelo.',
    body: 'Email ',
    bodyMid: ' to schedule a 30-minute setup call, or follow the ',
    guide: 'Onboarding setup guide',
    bodyTail: ' to continue on your own.',
    dismiss: 'dismiss',
  };
  return (
    <div style={{
      background: CL.cream, border: `1px solid ${CL.goldL}`, borderLeft: `3px solid ${CL.olive}`,
      borderRadius: 10, padding: '14px 16px', margin: '12px 14px',
      fontSize: 13, color: CL.bark, lineHeight: 1.5, position: 'relative',
    }}>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={L.dismiss}
        style={{
          position: 'absolute', top: 8, right: 10,
          background: 'none', border: 'none', color: CL.stone,
          fontSize: 16, lineHeight: 1, cursor: 'pointer',
        }}
      >×</button>
      <strong>{L.title}</strong><br />
      {L.body}<a href="mailto:hello@tavelo.app" style={{ color: CL.olive, fontWeight: 600 }}>hello@tavelo.app</a>{L.bodyMid}<span style={{ fontWeight: 600 }}>{L.guide}</span>{L.bodyTail}
    </div>
  );
}

function TrustedStar({ phone, guestTrusted, sz = 12 }) {
  const phoneKey = cleanPhone(phone);
  if (!phoneKey || !guestTrusted?.[phoneKey]) return null;
  return (
    <span title="Trusted guest" style={{ color: '#C9A96E', fontSize: sz, marginRight: 4 }}>
      ⭐
    </span>
  );
}

/* ═══ NAVIGATION CHROME (drawer + topbar + fab) ═══ */

function Wordmark({ diamondColor = CL.gold, onClick }) {
  const baseStyle = { fontFamily: FONT.body, fontSize: 12, letterSpacing: "2.4px", fontWeight: 700, color: CL.bark, display: "flex", alignItems: "center" };
  if (onClick) {
    return (
      <button onClick={onClick} aria-label="Home" style={{ ...baseStyle, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
        <Diamond color={diamondColor} mr={6} />TAVELO
      </button>
    );
  }
  return (
    <span style={baseStyle}>
      <Diamond color={diamondColor} mr={6} />TAVELO
    </span>
  );
}

function TrialPill({ property, lang, onClick }) {
  const cl = { bark:'#3D2B1F', gold:'#C9A96E', goldL:'#E8D5B0',
               cream:'#FFFDF8', stone:'#8B7D6B', terra:'#C0562B' };
  const status = property?.plan_status;
  if (!status || status === 'paid') return null;
  const now = Date.now();
  const trialEnd  = property.trial_ends_at  ? new Date(property.trial_ends_at).getTime()  : null;
  const graceEnd  = property.grace_ends_at  ? new Date(property.grace_ends_at).getTime()  : null;
  let effective = status;
  if (status === 'trial' && trialEnd && now >= trialEnd) effective = 'grace';
  if ((status === 'trial' || status === 'grace') && graceEnd && now >= graceEnd) effective = 'expired';

  let copy, bg, fg, bold = false;
  const daysLeftTrial = trialEnd ? Math.ceil((trialEnd - now) / 86400000) : 0;
  const daysLeftGrace = graceEnd ? Math.ceil((graceEnd - now) / 86400000) : 0;

  if (effective === 'trial') {
    copy = lang === 'bg' ? `Пробен · още ${daysLeftTrial} дни` : `Trial · ${daysLeftTrial} days left`;
    if (daysLeftTrial <= 7) { bg = `${cl.terra}1F`; fg = cl.terra; }
    else                    { bg = cl.goldL;        fg = cl.bark;  }
  } else if (effective === 'grace') {
    copy = lang === 'bg'
      ? `Пробният период изтече · ${daysLeftGrace} дни до плащане`
      : `Trial ended · ${daysLeftGrace} days to upgrade`;
    bg = cl.terra; fg = cl.cream; bold = true;
  } else if (effective === 'expired') {
    copy = lang === 'bg' ? 'Само за преглед · свържете се с нас' : 'Read-only · contact us';
    bg = cl.stone; fg = cl.cream;
  } else if (effective === 'cancelled') {
    copy = lang === 'bg' ? 'Прекратен · свържете се с нас' : 'Cancelled · contact us';
    bg = cl.stone; fg = cl.cream;
  }
  if (!copy) return null;
  return (
    <button onClick={onClick}
      style={{ background: bg, color: fg, padding: '4px 10px',
               borderRadius: 999, fontSize: 12, fontWeight: bold ? 700 : 500,
               border: 'none', cursor: 'pointer' }}>
      {copy}
    </button>
  );
}

function TrialModal({ property, lang, onClose }) {
  if (!property) return null;
  const cl = { bark:'#3D2B1F', cream:'#FFFDF8', gold:'#C9A96E' };
  const status = property.plan_status;
  const now = Date.now();
  const trialEnd = property.trial_ends_at ? new Date(property.trial_ends_at).getTime() : null;
  const graceEnd = property.grace_ends_at ? new Date(property.grace_ends_at).getTime() : null;
  let effective = status;
  if (status === 'trial' && trialEnd && now >= trialEnd) effective = 'grace';
  if ((status === 'trial' || status === 'grace') && graceEnd && now >= graceEnd) effective = 'expired';
  const isReadOnly = effective === 'expired' || effective === 'cancelled';

  const formatTrialEnd = () => {
    if (!property.trial_ends_at) return '';
    const d = new Date(property.trial_ends_at);
    const fmt = new Intl.DateTimeFormat(lang === 'bg' ? 'bg-BG' : 'en-GB',
      { day:'numeric', month:'long', year:'numeric' });
    return fmt.format(d);
  };

  const headline = isReadOnly
    ? (lang === 'bg' ? 'Tavelo е в режим само за преглед.' : 'Tavelo is currently read-only.')
    : (lang === 'bg'
        ? `Безплатният Ви пробен период изтича на ${formatTrialEnd()}.`
        : `Your free trial ends on ${formatTrialEnd()}.`);

  const bodyEN = isReadOnly
    ? 'Email hello@tavelo.app to reactivate. Your data is safe.'
    : "Email hello@tavelo.app to continue using Tavelo. We won't lock your account during an active guest stay.";
  const bodyBG = isReadOnly
    ? 'Пишете ни на hello@tavelo.app, за да активираме отново. Данните Ви са в безопасност.'
    : 'Пишете ни на hello@tavelo.app, за да продължите да използвате Tavelo. Няма да заключим акаунта Ви, докато имате активни гости.';
  const body = lang === 'bg' ? bodyBG : bodyEN;
  const parts = body.split('hello@tavelo.app');

  return (
    <div role="dialog" onClick={onClose}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex',
               alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: cl.cream, padding: 24, borderRadius: 12, maxWidth: 420, margin: 16 }}>
        <h2 style={{ color: cl.bark, marginTop: 0 }}>{headline}</h2>
        <p style={{ color: cl.bark, lineHeight: 1.5 }}>
          {parts[0]}<a href="mailto:hello@tavelo.app">hello@tavelo.app</a>{parts[1]}
        </p>
        <button onClick={onClose}
          style={{ background: cl.bark, color: cl.cream, border: 'none',
                   padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>
          {lang === 'bg' ? 'Разбрано' : 'Got it'}
        </button>
      </div>
    </div>
  );
}

function TopBar({ onMenuClick, onLogoClick, property, lang, onTrialPillClick }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px 12px", background: CL.cream, position: "sticky", top: 0, zIndex: 50, borderBottom: `1px solid ${HAIRLINE}` }}>
      <button onClick={onMenuClick} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }} aria-label="Menu">
        <HamburgerIcon />
      </button>
      <Wordmark onClick={onLogoClick} />
      <TrialPill property={property} lang={lang} onClick={onTrialPillClick} />
    </div>
  );
}

function Fab({ onClick, ariaLabel = "New booking" }) {
  return (
    <button onClick={onClick} aria-label={ariaLabel} style={{
      position: "fixed", right: 22, bottom: 22, width: 52, height: 52, borderRadius: 999,
      background: CL.bark, color: CL.cream, border: "none", cursor: "pointer", padding: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 6px 18px rgba(61,43,31,0.32)", zIndex: 40,
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </button>
  );
}

function Drawer({ open, onClose, currentTab, onSelectTab, property, lang, toggleLang, onSignOut, hasAnalytics }) {
  if (!open) return null;
  const labels = lang === "bg"
    ? { today: "Днес", occupancy: "Календар", directory: "Гости", rooms: "Стаи", analytics: "Анализи", settings: "Настройки", signOut: "Изход" }
    : { today: "Today", occupancy: "Calendar", directory: "Guests", rooms: "Rooms", analytics: "Analytics", settings: "Settings", signOut: "Sign out" };
  const items = [
    { key: "today",     label: labels.today },
    { key: "occupancy", label: labels.occupancy },
    { key: "directory", label: labels.directory },
    { key: "rooms",     label: labels.rooms },
  ];
  if (hasAnalytics) items.push({ key: "analytics", label: labels.analytics });
  items.push({ key: "settings", label: labels.settings });

  return (
    <div role="dialog" aria-label="Navigation menu" style={{
      position: "fixed", inset: 0,
      background: CL.cream, zIndex: 101, display: "flex", flexDirection: "column",
    }}>
      {/* Header — wordmark left, large × close right */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 22px 18px" }}>
        <Wordmark diamondColor={CL.olive} onClick={() => { onSelectTab('today'); onClose(); }} />
        <button onClick={onClose} aria-label="Close menu" style={{
          background: "none", border: "none", color: CL.olive,
          fontFamily: FONT.display, fontSize: 28, cursor: "pointer", padding: 0, lineHeight: 1, width: 28, height: 28,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>×</button>
      </div>

      {/* Property pill — bigger Cormorant for the full-screen drawer */}
      {property?.name && (
        <div style={{ padding: "10px 22px 28px" }}>
          <div style={{
            background: CL.sand, border: `1px solid ${CL.goldL}`, borderRadius: 8,
            padding: "16px 18px", color: CL.bark,
            fontFamily: FONT.display, fontSize: 22, fontWeight: 500, textAlign: "center",
          }}>
            {property.name}
          </div>
        </div>
      )}

      {/* Nav items — hairline-divided, larger glyphs + bigger labels */}
      <nav style={{ flex: 1, padding: "0 22px" }}>
        {items.map(it => {
          const active = currentTab === it.key;
          return (
            <button key={it.key} onClick={() => onSelectTab(it.key)} style={{
              width: "100%", display: "flex", alignItems: "center", gap: 16,
              background: "none", border: "none",
              borderBottom: `1px solid ${HAIRLINE}`,
              padding: "16px 0", cursor: "pointer",
              color: active ? CL.olive : CL.bark, fontFamily: FONT.body,
              fontSize: 17, fontWeight: active ? 600 : 500, textAlign: "left",
            }}>
              <span style={{
                width: 18, height: 18, borderRadius: 4, boxSizing: "border-box",
                border: `1.5px solid ${CL.olive}`,
                background: active ? CL.olive : "transparent",
                opacity: active ? 1 : 0.55,
                flexShrink: 0,
              }} />
              {it.label}
            </button>
          );
        })}
      </nav>

      {/* Footer — lang toggle (kept) + small sign-out */}
      <div style={{ padding: "20px 22px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
        <button onClick={toggleLang} style={{
          background: "none", border: `1px solid ${CL.goldL}`, borderRadius: 8,
          padding: "12px 14px", fontSize: 13, color: CL.stone, fontWeight: 600, cursor: "pointer", letterSpacing: 0.4,
        }}>
          {lang === "bg" ? "🇬🇧 English" : "🇧🇬 Български"}
        </button>
        <button onClick={onSignOut} style={{
          background: "none", border: "none", color: CL.stone,
          fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer", textAlign: "left", padding: "4px 0",
        }}>
          {labels.signOut}
        </button>
      </div>
    </div>
  );
}

/* ═══ GUEST DIRECTORY BUILDER ═══ */
function buildDir(all) {
  const m = {};
  for (const b of all) {
    const k = cleanPhone(b.phone);
    if (!k || k.length < 5) continue;
    if (!m[k]) m[k] = { name: b.guestName, phone: b.phone, email: b.email || "", stays: [], totalSpend: 0, roomIds: [] };
    const e = m[k];
    if (b.email && !e.email) e.email = b.email;
    e.stays.push({ id: b.id, checkIn: b.checkIn, checkOut: b.checkOut, roomId: b.roomId, price: Number(b.price || 0), source: b.source, nights: nightCount(b.checkIn, b.checkOut) });
    e.totalSpend += Number(b.price || 0);
    if (!e.roomIds.includes(b.roomId)) e.roomIds.push(b.roomId);
  }
  for (const k of Object.keys(m)) m[k].stays.sort((a, b) => (b.checkIn > a.checkIn ? 1 : -1));
  return m;
}

/* ═══ RESPONSIVE HOOK ═══ */
function useScreenSize() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const isTablet = w >= 768;
  const isDesktop = w >= 1100;
  // Cell width and room label scale up on larger screens
  const cellW = isDesktop ? 38 : isTablet ? 32 : 25;
  const labelW = isDesktop ? 80 : isTablet ? 70 : 60;
  const cardPad = isTablet ? "16px 22px" : "14px 16px";
  const maxW = isDesktop ? 1200 : isTablet ? 900 : "100%";
  return { w, isTablet, isDesktop, cellW, labelW, cardPad, maxW };
}

/* ═══════════════════════════════ */
/* ═══ MAIN APP ═══════════════ */
/* ═══════════════════════════════ */
export default function App() {
  const [lang, setLang] = useState("en");
  const scr = useScreenSize();
  const [user, setUser] = useState(null);
  const [property, setProperty] = useState(null);
  const canEdit = useMemo(() => {
    if (!property) return false;
    const { plan_status, trial_ends_at, grace_ends_at } = property;
    const now = Date.now();
    if (plan_status === 'paid') return true;
    if (plan_status === 'expired' || plan_status === 'cancelled') return false;
    if (plan_status === 'trial') {
      if (trial_ends_at && now >= new Date(trial_ends_at).getTime()) {
        if (grace_ends_at && now < new Date(grace_ends_at).getTime()) return true;
        return false;
      }
      return true;
    }
    if (plan_status === 'grace') {
      if (grace_ends_at && now >= new Date(grace_ends_at).getTime()) return false;
      return true;
    }
    return false;
  }, [property]);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginErr, setLoginErr] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [tab, setTab] = useState("today");
  const [rooms, setRooms] = useState([]);
  const aptIds = useMemo(() => new Set(rooms.filter(r => r.type === 'apartment').map(r => r.id)), [rooms]);
  function isApt(id) { return aptIds.has(id); }
  const [bks, setBks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [formInit, setFormInit] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [calMonth, setCalMonth] = useState(() => { const _d = new Date(); return [_d.getFullYear(), _d.getMonth()]; });
  const [viewBk, setViewBk] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bkKebabOpen, setBkKebabOpen] = useState(false);
  // Today: which square (if any) is expanded into a detail panel below the squares row.
  // null = collapsed (only In-house list shown). Tapping the same square toggles it shut.
  const [expandedSquare, setExpandedSquare] = useState(null);
  const [viewGuest, setViewGuest] = useState(null);
  const [guestEdit, setGuestEdit] = useState(null); // { name, phone, contact } draft, or null
  const [guestDeleteConfirm, setGuestDeleteConfirm] = useState(false);
  const [guestSearch, setGuestSearch] = useState("");
  const [toast, setToast] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [swapPair, setSwapPair] = useState(null);
  const [bulkEdit, setBulkEdit] = useState(null);
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [roomDraft, setRoomDraft] = useState(null);
  const [editCustomOpen, setEditCustomOpen] = useState(false);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(null); // room object or null
  const [deleteConfirm, setDeleteConfirm] = useState(null); // room object or null
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [occView, setOccView] = useState("month");
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1);
    return isoToday(d);
  });
  const [guestNotes, setGuestNotes] = useState({});
  const [guestTrusted, setGuestTrusted] = useState({});
  const [guestLangs, setGuestLangs] = useState({});
  const [bookingRequests, setBookingRequests] = useState([]);
  const [pastRequests, setPastRequests] = useState([]);
  const [showRequestHistory, setShowRequestHistory] = useState(false);
  const [guestContactPrefs, setGuestContactPrefs] = useState({});
  const [welcomeDismissed, setWelcomeDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(WELCOME_DISMISS_KEY) === '1'
  );
  function dismissWelcome() {
    localStorage.setItem(WELCOME_DISMISS_KEY, '1');
    setWelcomeDismissed(true);
  }

  const t = LANG[lang] || LANG.en;
  const showToast = useCallback(m => { setToast({ message: m, undo: null }); setTimeout(() => setToast(null), 2500); }, []);
  const showToastWithUndo = useCallback((message, opts = {}) => {
    setToast({ message, undo: opts.undo || null });
    setTimeout(() => setToast(prev => (prev && prev.message === message ? null : prev)), 8000);
  }, []);
  const today = isoToday(new Date());

  // ── Auth + Data Loading ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        loadPropertyData(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        setUser(session.user);
        if (event === 'SIGNED_IN') loadPropertyData(session.user.id);
      } else {
        setUser(null);
        setProperty(null);
        setRooms([]);
        setBks([]);
        setGuestNotes({});
        setGuestTrusted({});
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadPropertyData(userId) {
    setLoading(true);
    setLoadError(null);
    // .maybeSingle() after limit(1) returns null cleanly when the user has
    // no property yet. Earlier code used .single(), which returns PGRST116
    // for both "0 rows" and "2+ rows" — silently treating a duplicate-row
    // state as "no property" and re-mounting the wizard. A UNIQUE constraint
    // on owner_id now prevents 2+ rows, but the defensive ordering+limit
    // keeps this safe if the constraint is ever relaxed.
    const { data: prop, error: propError } = await supabase
      .from('properties')
      .select('*')
      .eq('owner_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (propError) {
      Sentry.captureException(propError);
      setLoadError(propError);
      setLoading(false);
      return;
    }

    if (!prop) { setLoading(false); return; }
    setProperty(prop);
    Sentry.setTag('property_id', prop.id);
    supabase.rpc('touch_last_seen', { property_id_in: prop.id }).then(() => {});
    setLang((prop.lang || 'en').split(',')[0]);

    const results = await Promise.all([
      supabase.from('rooms').select('*').eq('property_id', prop.id).order('sort_order'),
      supabase.from('bookings').select('*').eq('property_id', prop.id),
      supabase.from('guest_notes').select('*').eq('property_id', prop.id),
      supabase.from('booking_requests').select('*').eq('property_id', prop.id).order('created_at', { ascending: false }),
    ]);

    const failed = results.filter(r => r.error);
    if (failed.length > 0) {
      failed.forEach(r => Sentry.captureException(r.error));
      setLoadError(failed[0].error);
      setLoading(false);
      return;
    }

    const [{ data: roomRows }, { data: bkRows }, { data: noteRows }, { data: reqRows }] = results;

    setRooms((roomRows || []).map(roomFromDb));
    setBks((bkRows || []).map(bookingFromDb));
    const notesMap = {};
    const prefsMap = {};
    const trustedMap = {};
    const langsMap = {};
    for (const n of (noteRows || [])) {
      notesMap[n.phone_key] = n.note;
      if (n.preferred_contact) prefsMap[n.phone_key] = n.preferred_contact;
      if (n.trusted) trustedMap[n.phone_key] = true;
      if (n.lang) langsMap[n.phone_key] = n.lang;
    }
    setGuestNotes(notesMap);
    setGuestContactPrefs(prefsMap);
    setGuestTrusted(trustedMap);
    setGuestLangs(langsMap);
    setBookingRequests((reqRows || []).filter(r => r.status === 'pending'));
    setPastRequests((reqRows || []).filter(r => r.status !== 'pending'));
    setLoading(false);
  }

  async function saveRooms(updatedRooms) {
    if (!canEdit) return;
    setRooms(updatedRooms);
    const rows = updatedRooms.map(r => roomToDb(r, property.id));
    const { error } = await supabase.from('rooms').upsert(rows);
    if (error) showToast('Error saving rooms');
  }
  async function toggleLang() {
    const prev = lang;
    const nl = lang === 'en' ? 'bg' : 'en';
    setLang(nl);
    if (!property) return;
    const { error } = await supabase.from('properties').update({ lang: nl }).eq('id', property.id);
    if (error) {
      setLang(prev);
      showToast(localiseError(error, prev));
    }
  }
  async function saveGuestNote(phoneKey, note) {
    if (!canEdit) return;
    setGuestNotes(prev => ({ ...prev, [phoneKey]: note }));
    const { error } = await supabase.from('guest_notes').upsert({
      property_id: property.id,
      phone_key: phoneKey,
      note,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'property_id,phone_key' });
    if (error) showToast('Error saving note');
  }

  async function saveTrusted(phoneKey, trusted) {
    if (!canEdit) return;
    setGuestTrusted(prev => {
      const next = { ...prev };
      if (trusted) next[phoneKey] = true;
      else delete next[phoneKey];
      return next;
    });
    const { error } = await supabase.from('guest_notes').upsert({
      property_id: property.id,
      phone_key: phoneKey,
      trusted,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'property_id,phone_key' });
    if (error) {
      showToast('Error saving trusted flag');
      return;
    }
    showToast(trusted
      ? (lang === 'bg' ? '⭐ Маркиран като доверен, напомнянията за депозит се пропускат' : '⭐ Marked as trusted — deposit reminders will be skipped')
      : (lang === 'bg' ? 'Премахнат като доверен' : 'Unmarked as trusted'));
  }


  // ── Derived ──
  const rName = useCallback(id => rooms.find(r => r.id === id)?.name || id, [rooms]);
  const rType = useCallback(id => rooms.find(r => r.id === id)?.type || "", [rooms]);
  // In-house = guests staying tonight. A guest checking out today has left (or is
  // leaving) and frees their room, so they are NOT counted. Excludes cancelled/no-show.
  const curGuests = useMemo(() => bks.filter(b =>
    b.checkIn <= today && b.checkOut > today
    && b.status !== 'cancelled' && b.status !== 'no_show'
  ), [bks, today]);
  // Distinct rooms occupied tonight — drives the Today occupancy stat and the Rooms tab badge.
  const occIds = useMemo(() => new Set(curGuests.map(b => b.roomId)), [curGuests]);
  // Operational views (Today, Calendar, In-house) hide cancelled + no-show.
  // Analytics still sees them in `bks` so revenue tracking stays accurate.
  const isLive = (b) => b.status !== 'cancelled' && b.status !== 'no_show';
  const todayIn  = useMemo(() => bks.filter(b => isLive(b) && b.checkIn  === today), [bks, today]);
  const todayOut = useMemo(() => bks.filter(b => isLive(b) && b.checkOut === today), [bks, today]);
  const gDir = useMemo(() => buildDir(bks), [bks]);
  const multiBookingLabels = useMemo(() => buildMultiBookingLabels(bks), [bks]);
  const gList = useMemo(() => Object.values(gDir).sort((a, b) => b.stays.length - a.stays.length), [gDir]);
  const realBks = bks;
  const totalRev = useMemo(() => realBks.reduce((s, b) => s + (Number(b.price) || 0), 0), [realBks]);

  // ── Actions ──
  function startNew() { if (!canEdit) return; setFormInit({ id: uid(), guestName: "", phone: "", email: "", roomId: "", checkIn: "", checkOut: "", price: "", pricePerNight: "", paymentStatus: "Unpaid", source: lang === "bg" ? "Директно" : "Direct", adults: 1, children: [], extraBeds: 0, depositAmount: "", extras: [], note: "", status: "confirmed", preferredContact: null, createdAt: new Date().toISOString() }); setTab("form"); }
  function startEdit(b) { const children = Array.isArray(b.children) ? b.children : Array.from({ length: Number(b.children) || 0 }, () => ({ age: "" })); setFormInit({ ...b, pricePerNight: b.pricePerNight || "", children, extraBeds: b.extraBeds || 0, depositAmount: b.depositAmount || "", extras: b.extras || [], preferredContact: b.preferredContact || null }); setViewBk(null); setTab("form"); }

  async function startFromRequest(req) {
    if (!canEdit) return;
    const rr = req.rooms_requested?.[0] || {};
    const propExtras = property?.extras ?? [];
    const sofaEx = propExtras.find(isSofaBedExtra);
    const cotEx = propExtras.find(isCotExtra);
    const occEx = propExtras.find(isOccasionExtra);
    const preExtras = [
      ...(rr.extra_beds > 0 && sofaEx ? [extraLabel(sofaEx, lang)] : []),
      ...(rr.cot && cotEx ? [extraLabel(cotEx, lang)] : []),
      ...(rr.special_occasion && occEx ? [extraLabel(occEx, lang)] : []),
    ];
    setFormInit({
      id: uid(),
      guestName: req.guest_name,
      phone: req.phone || '',
      email: req.email || '',
      roomId: '',
      checkIn: req.check_in,
      checkOut: req.check_out,
      price: '',
      pricePerNight: '',
      paymentStatus: 'Unpaid',
      source: lang === 'bg' ? 'Директно' : 'Direct',
      adults: req.adults,
      children: req.children || [],
      depositAmount: '',
      extras: preExtras,
      note: req.message || '',
      lang: req.lang || null,
      status: 'confirmed',
      preferredContact: req.preferred_contact || null,
      breakfastIncluded: !!(rr.breakfast),
      breakfastPrice: Number(rr.breakfast_price) || 0,
      extraBeds: (rr.extra_beds || 0) > 0 ? 1 : 0,
      createdAt: new Date().toISOString(),
      _requestedType: rr.type || null,
    });
    const { error } = await supabase.from('booking_requests').update({ status: 'approved' }).eq('id', req.id);
    if (error) {
      showToast(lang === 'bg' ? 'Грешка при потвърждаване' : 'Error approving request');
      return;
    }
    setBookingRequests(prev => prev.filter(r => r.id !== req.id));
    setPastRequests(prev => [{ ...req, status: 'approved' }, ...prev]);
    setTab('form');
  }

  function startRoomFromRequest(req, roomIndex) {
    const roomReq = req.rooms_requested[roomIndex];
    const children = Array.from({ length: roomReq.children || 0 }, (_, j) => ({
      age: String(roomReq.childAges?.[j] ?? ''),
    }));
    const propExtras = property?.extras ?? [];
    const sofaEx = propExtras.find(isSofaBedExtra);
    const cotEx = propExtras.find(isCotExtra);
    const occEx = propExtras.find(isOccasionExtra);
    const preExtras = [
      ...(roomReq.extra_beds > 0 && sofaEx ? [extraLabel(sofaEx, lang)] : []),
      ...(roomReq.cot && cotEx ? [extraLabel(cotEx, lang)] : []),
      ...(roomReq.special_occasion && occEx ? [extraLabel(occEx, lang)] : []),
    ];
    setFormInit({
      id: uid(),
      guestName: req.guest_name,
      phone: req.phone || '',
      email: req.email || '',
      roomId: '',
      checkIn: req.check_in,
      checkOut: req.check_out,
      price: '',
      pricePerNight: '',
      paymentStatus: 'Unpaid',
      source: lang === 'bg' ? 'Директно' : 'Direct',
      adults: roomReq.adults,
      children,
      depositAmount: '',
      extras: preExtras,
      note: req.message || '',
      lang: req.lang || null,
      status: 'confirmed',
      preferredContact: req.preferred_contact || null,
      breakfastIncluded: !!roomReq.breakfast,
      breakfastPrice: Number(roomReq.breakfast_price) || 0,
      extraBeds: (roomReq.extra_beds || 0) > 0 ? 1 : 0,
      createdAt: new Date().toISOString(),
      _requestId: req.id,
      _roomIndex: roomIndex,
      _requestedType: roomReq.type || null,
    });
    setTab('form');
  }

  async function onRoomAssigned(req) {
    if (!canEdit) return;
    const newCount = (req.rooms_assigned || 0) + 1;
    const isComplete = newCount >= req.rooms_requested.length;

    const { error } = await supabase.from('booking_requests')
      .update(isComplete ? { rooms_assigned: newCount, status: 'approved' } : { rooms_assigned: newCount })
      .eq('id', req.id);

    if (error) { showToast(lang === 'bg' ? 'Грешка при запис' : 'Error saving progress'); return; }

    if (isComplete) {
      setBookingRequests(prev => prev.filter(r => r.id !== req.id));
      setPastRequests(prev => [{ ...req, rooms_assigned: newCount, status: 'approved' }, ...prev]);
      showToast(lang === 'bg' ? 'Всички стаи са потвърдени' : 'All rooms assigned — booking confirmed');
    } else {
      setBookingRequests(prev =>
        prev.map(r => r.id === req.id ? { ...r, rooms_assigned: newCount } : r)
      );
      showToast(
        lang === 'bg'
          ? `Стая ${newCount} потвърдена, остава ${req.rooms_requested.length - newCount}`
          : `Room ${newCount} assigned — ${req.rooms_requested.length - newCount} remaining`
      );
    }
  }

  async function declineRequest(req) {
    if (!canEdit) return;
    const { error } = await supabase.from('booking_requests').update({ status: 'declined' }).eq('id', req.id);
    if (error) { showToast(localiseError(error, lang)); return; }
    setBookingRequests(prev => prev.filter(r => r.id !== req.id));
    setPastRequests(prev => [{ ...req, status: 'declined' }, ...prev]);
    // Open a pre-filled message if phone available (not for 'phone' — owner must call manually)
    if (req.phone && req.preferred_contact === 'viber') {
      const msg = `Dear ${req.guest_name}, unfortunately we're fully booked for your requested dates (${req.check_in} – ${req.check_out}). We hope to welcome you another time.`;
      const phone = req.phone.replace(/[^0-9]/g, '');
      navigator.clipboard.writeText(msg);
      window.location.href = `viber://chat?number=${phone}`;
    } else if (req.phone && req.preferred_contact === 'whatsapp') {
      const msg = `Dear ${req.guest_name}, unfortunately we're fully booked for your requested dates (${req.check_in} – ${req.check_out}). We hope to welcome you another time.`;
      const phone = req.phone.replace(/[^0-9]/g, '');
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    } else if (req.email && req.preferred_contact === 'email') {
      const subject = `Re: Booking request for ${req.check_in}`;
      const body = `Dear ${req.guest_name}, unfortunately we're fully booked for your requested dates. We hope to welcome you another time.`;
      window.location.href = `mailto:${req.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    showToast('Request declined');
  }

  async function onFormSave(f) {
    if (!canEdit) return;
    const isEdit = bks.some(b => b.id === f.id);
    const room = rooms.find(r => r.id === f.roomId);
    const row = bookingToDb(f, property.id, property, room);
    const { error } = await supabase.from('bookings').upsert(row);
    if (error) { console.error('Booking save error:', error); showToast('Error saving booking'); return; }
    setBks(prev => {
      const i = prev.findIndex(b => b.id === f.id);
      return i >= 0 ? prev.map((b, j) => j === i ? f : b) : [...prev, f];
    });
    // Persist preferred_contact + lang back to guest_notes so the guest
    // directory and message tracker pick them up without waiting for the
    // backfill query to run.
    if (f.phone && (f.preferredContact || f.lang)) {
      const phoneKey = cleanPhone(f.phone);
      if (phoneKey) {
        const patch = {
          property_id: property.id,
          phone_key: phoneKey,
          updated_at: new Date().toISOString(),
        };
        if (f.preferredContact) {
          patch.preferred_contact = f.preferredContact;
          setGuestContactPrefs(prev => ({ ...prev, [phoneKey]: f.preferredContact }));
        }
        if (f.lang) {
          patch.lang = f.lang;
          setGuestLangs(prev => ({ ...prev, [phoneKey]: f.lang }));
        }
        supabase.from('guest_notes').upsert(patch, { onConflict: 'property_id,phone_key', ignoreDuplicates: false })
          .then(({ error }) => { if (error) Sentry.captureException(error); });
      }
    }
    if (f._requestId) {
      const req = bookingRequests.find(r => r.id === f._requestId);
      if (req) await onRoomAssigned(req);
    }
    setFormInit(null);
    if (!isEdit && !f._requestId) { setViewBk(f); }
    setTab('today');
    if (!f._requestId) showToast(isEdit ? t.updated : t.saved);
  }

  // ── Move/Swap ──
  function startMove(b) { setMoveTarget(b); setSwapPair(null); setViewBk(null); }
  function getConflicts(rid, ci, co, xid) {
    return bks.filter(b =>
      b.roomId === rid && b.id !== xid
      && b.status !== 'cancelled' && b.status !== 'no_show'
      && overlap(ci, co, b.checkIn, b.checkOut)
    );
  }
  async function execMove(bid, nrid) {
    if (!canEdit) return;
    const { error } = await supabase.from('bookings').update({ room_id: nrid }).eq('id', bid);
    if (error) { showToast('Error moving booking'); return; }
    setBks(prev => prev.map(b => b.id === bid ? { ...b, roomId: nrid } : b));
    setMoveTarget(null); showToast(t.moveOk);
  }
  async function execSwap(b1, nr1, b2, nr2) {
    if (!canEdit) return;
    // Swap via an atomic RPC: a plain upsert hits the INSERT RLS policy (which
    // needs property_id we don't send) and two sequential updates trip the
    // non-deferrable overlap constraint mid-swap. See migration 037.
    const { error } = await supabase.rpc('swap_booking_rooms', { b1, r1: nr1, b2, r2: nr2 });
    if (error) { showToast('Error swapping rooms'); return; }
    setBks(prev => prev.map(b => {
      if (b.id === b1) return { ...b, roomId: nr1 };
      if (b.id === b2) return { ...b, roomId: nr2 };
      return b;
    }));
    setMoveTarget(null); setSwapPair(null); showToast(t.swapOk);
  }
  function canSwap(blocker, tgtRoom) {
    return !bks.some(b =>
      b.roomId === tgtRoom && b.id !== moveTarget?.id && b.id !== blocker.id
      && b.status !== 'cancelled' && b.status !== 'no_show'
      && overlap(blocker.checkIn, blocker.checkOut, b.checkIn, b.checkOut)
    );
  }

  const [cY, cM] = calMonth;
  const daysInMonth = new Date(cY, cM + 1, 0).getDate();

  // ── Booking row renderer ──
  function renderRow(b, onClick, noteType) {
    const isRepeat = gDir[cleanPhone(b.phone)]?.stays.length > 1;
    const isBcom = b.source === "Booking.com";
    const gr = Number(b.price) || 0;
    const trackComm = property?.track_ota_commission ?? true;
    const cm = (isBcom && trackComm) ? bcomCalc(gr, property?.bcom_commission ?? 15, property?.bcom_vcc_fee_pct ?? 1.5) : null;
    // Single-note model — render b.note for both check-in and check-out rows.
    // Falls back to legacy notesCi/notesCo for un-migrated rows during the
    // migration-012 transition window.
    const noteText = b.note || b.notesCi || b.notesCo || "";
    return (
      <div key={b.id} style={{ ...st.card, cursor: "pointer" }} onClick={onClick}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: CL.bark }}>
                <TrustedStar phone={b.phone} guestTrusted={guestTrusted} />{b.guestName}{multiBookingLabels[b.id] ? ` (${multiBookingLabels[b.id]})` : ''}
              </span>
              {isRepeat && <span style={{ fontSize: 12 }}>⭐</span>}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#7A5C2E" }}>
              {isApt(b.roomId) ? t.aptLabel : t.roomLabel} {rName(b.roomId)}
              {b.extras && b.extras.length > 0 && <span style={{ color: "#B8431A", fontWeight: 700 }}> · {b.extras.map(k => extraLabel(k, lang)).filter(Boolean).join(" · ")}</span>}
            </div>
            <div style={{ fontSize: 12, color: CL.stone }}>{fmtS(b.checkIn)}→{fmtS(b.checkOut)} · {nightCount(b.checkIn, b.checkOut)}{t.nShort}</div>
            <div style={{ fontSize: 12, color: CL.stone }}>{fmtGuests(b, t)}</div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
            <span style={badge(payCol(b.paymentStatus))}>{t[b.paymentStatus.toLowerCase()] || b.paymentStatus}</span>
            {isBcom && cm
              ? <div style={{ fontSize: 11, color: CL.sea, marginTop: 2 }}>{cur}{gr} → <strong>{cur}{cm.net}</strong></div>
              : <div style={{ fontSize: 13, fontWeight: 700, color: CL.bark, marginTop: 2 }}>{cur}{gr}</div>
            }
          </div>
        </div>
        {noteText && (
          <div style={{ marginTop: 6, background: "#FFF8E1", borderRadius: 6, padding: "5px 10px", fontSize: 12, color: "#8B6914" }}>
            📝 {noteText}
          </div>
        )}
      </div>
    );
  }

  // ════════ LOGIN ════════
  async function handleLogin(e) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginErr('');
    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: loginPassword,
    });
    if (error) {
      setLoginErr(lang === 'bg' ? 'Грешен имейл или парола' : 'Incorrect email or password');
      setLoginPassword('');
    }
    setLoginLoading(false);
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginErr('');
    const { error } = await supabase.auth.resetPasswordForEmail(loginEmail, {
      redirectTo: window.location.origin + '/reset-password',
    });
    if (error) {
      setLoginErr(localiseError(error, lang));
    } else {
      setForgotMsg(lang === 'bg'
        ? 'Проверете имейла си за линк за смяна на паролата.'
        : 'Check your email for a password reset link.');
    }
    setLoginLoading(false);
  }

  // Onboarding gate — runs after login, before the normal app shell.
  const wizardStep = user && !loading ? wizardStepFromProperty(property) : null;
  if (user && wizardStep) {
    return (
      <OnboardingWizard
        userId={user.id}
        userEmail={user.email}
        property={property}
        canEdit={canEdit}
        onPropertyChange={(next) => {
          setProperty(next);
          if (next?.onboarding_step === 'done') {
            loadPropertyData(user.id);
          }
        }}
      />
    );
  }

  if (!user) {
    const fieldStyle = (focused, hasErr) => ({
      width: '100%', boxSizing: 'border-box',
      padding: '12px 14px',
      border: `1px solid ${hasErr ? CL.terra : focused ? CL.gold : CL.goldL}`,
      borderRadius: 10,
      background: '#FFFEF9',
      fontFamily: FONT.body, fontSize: 15, color: CL.bark,
      outline: 'none', transition: 'border-color 0.15s ease',
    });
    const primaryBtn = (disabled) => ({
      width: '100%', background: CL.bark, color: CL.cream, border: 'none',
      borderRadius: 10, padding: '13px 16px',
      fontFamily: FONT.body, fontSize: 13, fontWeight: 600,
      letterSpacing: '0.18em', textTransform: 'uppercase',
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1,
    });
    const linkBtn = {
      background: 'none', border: 'none', padding: 0,
      color: CL.stone, fontFamily: FONT.body, fontSize: 12,
      letterSpacing: '0.04em', cursor: 'pointer',
    };
    return (
      <div style={{ minHeight: '100svh', background: CL.sand, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{
          width: '100%', maxWidth: 360,
          background: CL.cream, border: `1px solid ${CL.goldL}`, borderRadius: 14,
          padding: '36px 28px',
          boxShadow: '0 6px 28px rgba(61,43,31,0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 32 }}>
            <Diamond color={CL.gold} mr={8} size={9} />
            <span style={{ fontFamily: FONT.body, fontSize: 22, letterSpacing: '2.4px', fontWeight: 700, color: CL.bark }}>TAVELO</span>
          </div>

          {forgotMode ? (
            forgotMsg ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FONT.body, color: CL.bark, fontSize: 14, lineHeight: 1.5, marginBottom: 24 }}>{forgotMsg}</div>
                <button type="button" style={linkBtn} onClick={() => { setForgotMode(false); setForgotMsg(''); }}>
                  {lang === 'bg' ? '← Обратно към вход' : '← Back to sign in'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <div style={{ fontFamily: FONT.body, fontSize: 13, color: CL.stone, marginBottom: 14, textAlign: 'center', lineHeight: 1.5 }}>
                  {lang === 'bg' ? 'Въведете имейла си за смяна на паролата' : 'Enter your email to reset your password'}
                </div>
                <input
                  style={fieldStyle(focusedField === 'email', !!loginErr)}
                  type="email" placeholder="Email" value={loginEmail}
                  onChange={e => { setLoginEmail(e.target.value); setLoginErr(''); }}
                  onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                  autoFocus required autoComplete="email"
                />
                {loginErr && <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13, color: CL.terra, fontFamily: FONT.body }}>{loginErr}</div>}
                <button type="submit" disabled={loginLoading} style={{ ...primaryBtn(loginLoading), marginTop: 16 }}>
                  {loginLoading ? '...' : (lang === 'bg' ? 'Изпрати линк' : 'Send reset link')}
                </button>
                <div style={{ textAlign: 'center', marginTop: 18 }}>
                  <button type="button" style={linkBtn} onClick={() => { setForgotMode(false); setLoginErr(''); }}>
                    {lang === 'bg' ? '← Обратно към вход' : '← Back to sign in'}
                  </button>
                </div>
              </form>
            )
          ) : (
            <form onSubmit={handleLogin}>
              <input
                style={fieldStyle(focusedField === 'email', !!loginErr)}
                type="email" placeholder="Email" value={loginEmail}
                onChange={e => { setLoginEmail(e.target.value); setLoginErr(''); }}
                onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                autoFocus required autoComplete="email"
              />
              <input
                style={{ ...fieldStyle(focusedField === 'password', !!loginErr), marginTop: 10 }}
                type="password" placeholder="••••••••" value={loginPassword}
                onChange={e => { setLoginPassword(e.target.value); setLoginErr(''); }}
                onFocus={() => setFocusedField('password')} onBlur={() => setFocusedField(null)}
                required autoComplete="current-password"
              />
              {loginErr && <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13, color: CL.terra, fontFamily: FONT.body }}>{loginErr}</div>}
              <button type="submit" disabled={loginLoading} style={{ ...primaryBtn(loginLoading), marginTop: 16 }}>
                {loginLoading ? '...' : (lang === 'bg' ? 'Вход' : 'Sign in')}
              </button>
              <div style={{ textAlign: 'center', marginTop: 18 }}>
                <button type="button" style={linkBtn} onClick={() => { setForgotMode(true); setLoginErr(''); }}>
                  {lang === 'bg' ? 'Забравена парола?' : 'Forgot password?'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ════════ LOAD-ERROR SPLASH ════════
  // Shown when loadPropertyData hits a SELECT error — without this the user
  // would see an empty dashboard reading "0 rooms / 0 bookings" and conclude
  // their data vanished. The retry button re-runs the loader.
  if (loadError && !loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: CL.sand, padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 360 }}>
        <p style={{ color: CL.bark, fontSize: 17, fontFamily: FONT.display, marginBottom: 8 }}>
          {lang === 'bg' ? 'Не успяхме да заредим данните.' : "We couldn't load your data."}
        </p>
        <p style={{ color: CL.stone, fontSize: 13, marginBottom: 20 }}>
          {localiseError(loadError, lang)}
        </p>
        <button
          onClick={() => { setLoadError(null); if (user) loadPropertyData(user.id); }}
          style={{ background: CL.bark, color: CL.cream, border: 'none',
                   borderRadius: 8, padding: '10px 22px', fontSize: 13,
                   fontWeight: 600, letterSpacing: '0.06em', cursor: 'pointer' }}>
          {lang === 'bg' ? 'Опитай отново' : 'Try again'}
        </button>
      </div>
    </div>
  );

  // ════════ LOADING ════════
  if (loading && !property) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: CL.sand }}>
      <p style={{ color: CL.stone, fontSize: 16, fontFamily: FONT.display, letterSpacing: 1 }}>{property?.name || 'Hotel Manager'}</p>
    </div>
  );

  // ════════ DETERMINE CONTENT ════════
  const cur = curSym(property?.currency);
  let content = null;

  // ── MOVE/SWAP VIEW ──
  if (moveTarget) {
    const bk = moveTarget;
    const others = rooms.filter(r => r.id !== bk.roomId && !r.archived);
    const freeRooms = others.filter(r => getConflicts(r.id, bk.checkIn, bk.checkOut, bk.id).length === 0);
    const occRooms = others.filter(r => getConflicts(r.id, bk.checkIn, bk.checkOut, bk.id).length > 0);

    if (swapPair) {
      const { blocker, targetRoomId } = swapPair;
      const canDo = canSwap(blocker, bk.roomId);
      content = (
        <div>
          <div style={st.hdr}><button onClick={() => setSwapPair(null)} style={st.hBtn}><Ic n="left" sz={20} /></button><span style={{ fontSize: 17, fontWeight: 600 }}>{t.swapConf}</span><div style={{ width: 28 }} /></div>
          <div style={st.card}>
            <div style={{ background: "#E8EFDE", borderRadius: 10, padding: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: CL.bark }}>📦 {bk.guestName}</div>
              <div style={{ fontSize: 12, color: CL.olive }}>{rName(bk.roomId)} → <strong>{rName(targetRoomId)}</strong></div>
            </div>
            <div style={{ background: "#FFF8E1", borderRadius: 10, padding: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: CL.bark }}>📦 {blocker.guestName}</div>
              <div style={{ fontSize: 12, color: "#B8860B" }}>{rName(blocker.roomId)} → <strong>{rName(bk.roomId)}</strong></div>
            </div>
          </div>
          {canDo
            ? <div style={{ padding: "0 14px" }}><button onClick={() => execSwap(bk.id, targetRoomId, blocker.id, bk.roomId)} style={{ ...st.btnP, fontSize: 17, padding: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Ic n="swap" sz={20} /> {t.swapConf}</button></div>
            : <div style={{ ...st.card, background: "#FFF5F5", border: "1px solid " + CL.terra }}><div style={{ fontSize: 14, fontWeight: 600, color: CL.terra }}>{t.cantSwap}</div></div>
          }
        </div>
      );
    } else {
      content = (
        <div>
          <div style={st.hdr}><button onClick={() => setMoveTarget(null)} style={st.hBtn}><Ic n="left" sz={20} /></button><span style={{ fontSize: 17, fontWeight: 600 }}>{t.moveTitle}</span><div style={{ width: 28 }} /></div>
          <div style={{ ...st.card, background: CL.sand }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: CL.bark }}>{bk.guestName}</div>
            <div style={{ fontSize: 13, color: CL.stone }}>{rName(bk.roomId)} · {fmtF(bk.checkIn)} → {fmtF(bk.checkOut)}</div>
          </div>
          {freeRooms.length > 0 && (
            <>
              <div style={st.sec}>{t.freeRms}</div>
              {freeRooms.map(r => (
                <div key={r.id} style={{ ...st.card, cursor: "pointer" }} onClick={() => execMove(bk.id, r.id)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div><div style={{ fontSize: 15, fontWeight: 700, color: CL.bark }}>{r.name}</div><div style={{ fontSize: 12, color: CL.stone }}>{r.type}</div></div>
                    <div style={{ background: "#E8EFDE", color: CL.olive, borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600 }}>→ {t.moveRm}</div>
                  </div>
                </div>
              ))}
            </>
          )}
          {occRooms.length > 0 && (
            <>
              <div style={st.sec}>{t.occRms}</div>
              {occRooms.map(r => {
                const confs = getConflicts(r.id, bk.checkIn, bk.checkOut, bk.id);
                return (
                  <div key={r.id} style={st.card}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: CL.bark, marginBottom: 6 }}>{r.name}</div>
                    {confs.map(bl => {
                      const ok = canSwap(bl, bk.roomId);
                      return (
                        <div key={bl.id} style={{ background: "#FFF8E1", borderRadius: 8, padding: "10px 12px", marginBottom: 6 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: CL.bark }}>{bl.guestName} · {fmtS(bl.checkIn)}→{fmtS(bl.checkOut)}</div>
                          {ok
                            ? <button onClick={() => setSwapPair({ blocker: bl, targetRoomId: r.id })} style={{ marginTop: 6, background: "#B8860B", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}><Ic n="swap" sz={16} /> {t.swapW}</button>
                            : <div style={{ marginTop: 4, fontSize: 12, color: CL.terra, fontWeight: 600 }}>{t.cantSwap}</div>
                          }
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>
      );
    }
  }

  // ── GUEST PROFILE ──
  else if (viewGuest) {
    const g = viewGuest;
    const phoneKey = cleanPhone(g.phone);
    const isReturning = g.stays.length >= 2;
    const totalNights = g.stays.reduce((s, x) => s + x.nights, 0);
    const pref = guestContactPrefs[phoneKey];
    // guest_notes.lang carries the guest's preferred language (migration 012).
    // Falls back to phone-country derivation while the column is still being
    // backfilled.
    const guestLang = (guestLangs[phoneKey] || (phoneKey.startsWith('359') ? 'bg' : 'en')).toUpperCase();
    const returningEyebrow = lang === 'bg'
      ? `★ ВРЪЩАЩ СЕ · ${g.stays.length} ${t.stays.toUpperCase()}`
      : `★ RETURNING · ${g.stays.length} STAYS`;

    function startNewFromGuest() {
      setFormInit({
        id: uid(), guestName: g.name, phone: g.phone, email: g.email || '',
        roomId: '', checkIn: '', checkOut: '', price: '', pricePerNight: '',
        paymentStatus: 'Unpaid',
        source: lang === 'bg' ? 'Директно' : 'Direct',
        adults: 1, children: [], extraBeds: 0, depositAmount: '',
        extras: [], note: '', status: 'confirmed',
        preferredContact: pref || null,
        createdAt: new Date().toISOString(),
      });
      setViewGuest(null);
      setTab('form');
    }

    function messageGuest() {
      const ch = (pref || 'whatsapp').toLowerCase();
      const cleaned = phoneKey;
      if (ch === 'whatsapp') window.open(`https://wa.me/${cleaned}`, '_blank');
      else if (ch === 'viber') window.open(`viber://chat?number=%2B${cleaned}`, '_blank');
      else if (ch === 'email' && g.email) window.location.href = `mailto:${g.email}`;
      else if (ch === 'phone' || ch === 'whatsapp') window.location.href = `tel:${g.phone}`;
    }

    async function saveGuestEdit() {
      if (!canEdit) return;
      const newName = (guestEdit.name || '').trim();
      const newPhone = (guestEdit.phone || '').trim();
      if (!newName) { showToast(lang === 'bg' ? 'Името е задължително' : 'Name is required'); return; }
      const newKey = cleanPhone(newPhone);
      const phoneChanged = newKey !== phoneKey;
      // Don't silently merge this guest into a different existing guest.
      if (phoneChanged && newKey && gDir[newKey]) {
        showToast(lang === 'bg' ? 'Друг гост вече използва този номер' : 'Another guest already uses that number');
        return;
      }
      const ids = bks.filter(b => cleanPhone(b.phone) === phoneKey).map(b => b.id);
      const patch = { guest_name: newName };
      if (phoneChanged) { patch.phone = newPhone; patch.phone_key = newKey; }
      else if (newPhone !== (g.phone || '')) { patch.phone = newPhone; } // formatting-only edit
      if (ids.length > 0) {
        const { error } = await supabase.from('bookings').update(patch).in('id', ids);
        if (error) { showToast(localiseError(error, lang)); return; }
      }
      // Move the saved note/contact row to the new phone key.
      if (phoneChanged) {
        const { error: nErr } = await supabase.from('guest_notes')
          .update({ phone_key: newKey }).eq('property_id', property.id).eq('phone_key', phoneKey);
        if (nErr) { showToast(localiseError(nErr, lang)); return; }
      }
      const writeKey = phoneChanged ? newKey : phoneKey;
      const newContact = guestEdit.contact || null;
      if (newContact !== ((pref || '').toLowerCase() || null)) {
        const { error: cErr } = await supabase.from('guest_notes').upsert({
          property_id: property.id, phone_key: writeKey,
          preferred_contact: newContact, updated_at: new Date().toISOString(),
        }, { onConflict: 'property_id,phone_key' });
        if (cErr) { showToast(localiseError(cErr, lang)); return; }
      }
      // Local state.
      setBks(prev => prev.map(b => ids.includes(b.id) ? { ...b, guestName: newName, phone: newPhone } : b));
      if (phoneChanged) {
        const rekey = (setter) => setter(prev => {
          if (!(phoneKey in prev)) return prev;
          const next = { ...prev }; next[newKey] = next[phoneKey]; delete next[phoneKey]; return next;
        });
        rekey(setGuestNotes); rekey(setGuestTrusted); rekey(setGuestLangs); rekey(setGuestContactPrefs);
      }
      setGuestContactPrefs(prev => {
        const next = { ...prev };
        if (newContact) next[writeKey] = newContact; else delete next[writeKey];
        return next;
      });
      setViewGuest({ ...g, name: newName, phone: newPhone });
      setGuestEdit(null);
      showToast(lang === 'bg' ? 'Запазено!' : 'Saved!');
    }

    async function deleteGuest() {
      if (!canEdit) return;
      const { error } = await supabase.from('guest_notes').delete()
        .eq('property_id', property.id).eq('phone_key', phoneKey);
      if (error) { showToast(localiseError(error, lang)); return; }
      const drop = (setter) => setter(prev => { const n = { ...prev }; delete n[phoneKey]; return n; });
      drop(setGuestNotes); drop(setGuestContactPrefs); drop(setGuestTrusted); drop(setGuestLangs);
      setGuestDeleteConfirm(false);
      setGuestEdit(null);
      setViewGuest(null);
      showToast(lang === 'bg' ? 'Гостът е изтрит' : 'Guest deleted');
    }

    const contactOptions = [
      { v: 'whatsapp', label: 'WhatsApp' },
      { v: 'viber', label: 'Viber' },
      { v: 'phone', label: lang === 'bg' ? 'Телефон' : 'Phone' },
      { v: 'email', label: lang === 'bg' ? 'Имейл' : 'Email' },
    ];

    content = (
      <div>
        {/* Drill-down header — back ‹ + TAVELO wordmark (matches booking detail) */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 12px', background: CL.cream, position: 'sticky', top: 0, zIndex: 50, borderBottom: `1px solid ${HAIRLINE}` }}>
          <button onClick={() => setViewGuest(null)} aria-label="Back" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 22, lineHeight: 1, color: CL.bark, width: 22 }}>‹</button>
          <Wordmark onClick={() => { setViewGuest(null); setTab('today'); setExpandedSquare(null); }} />
          <span style={{ width: 22 }} />
        </div>

        {/* Top summary card — avatar + name block + 3 Cormorant stats */}
        <div style={{ background: CL.cream, margin: '10px 14px', borderRadius: 14, border: `1px solid ${CL.goldL}`, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 14px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{
              width: 48, height: 48, borderRadius: '50%',
              border: `1px solid ${isReturning ? CL.gold : CL.goldL}`,
              background: CL.cream, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: FONT.display, fontSize: 22, fontWeight: 500, color: CL.bark, flexShrink: 0,
            }}>{g.name[0]}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {isReturning && (
                <div style={{ fontSize: 10, color: CL.olive, letterSpacing: 1.6, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>{returningEyebrow}</div>
              )}
              <div style={{ fontFamily: FONT.display, fontSize: 24, fontWeight: 500, color: CL.bark, lineHeight: 1.05, marginBottom: 4 }}>{g.name}</div>
              {g.phone && (
                <a href={`tel:${g.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: CL.bark, textDecoration: 'none', fontSize: 13.5, fontWeight: 500 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ color: CL.olive, flexShrink: 0 }} aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <span style={{ borderBottom: `1px solid ${CL.goldL}`, paddingBottom: 1 }}>{g.phone}</span>
                </a>
              )}
            </div>
            {canEdit && !guestEdit && (
              <button onClick={() => setGuestEdit({ name: g.name, phone: g.phone || '', contact: (pref || '').toLowerCase() })}
                style={{ background: 'none', border: `1px solid ${CL.goldL}`, borderRadius: 8, padding: '6px 12px', fontSize: 12, color: CL.stone, cursor: 'pointer', fontFamily: FONT.body, flexShrink: 0 }}>
                {lang === 'bg' ? 'Промени' : 'Edit'}
              </button>
            )}
          </div>

          {guestEdit && (
            <div style={{ borderTop: `1px solid ${CL.goldL}`, padding: '14px 16px', background: CL.sand }}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, marginBottom: 6, fontFamily: FONT.body }}>{lang === 'bg' ? 'Име' : 'Name'}</label>
                <input value={guestEdit.name} onChange={e => setGuestEdit(d => ({ ...d, name: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: `1.5px solid ${CL.goldL}`, borderRadius: 9, fontSize: 14, fontFamily: FONT.body, background: '#FFFEF9', outline: 'none', color: CL.bark }} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, marginBottom: 6, fontFamily: FONT.body }}>{lang === 'bg' ? 'Телефон' : 'Phone'}</label>
                <input value={guestEdit.phone} onChange={e => setGuestEdit(d => ({ ...d, phone: e.target.value }))} inputMode="tel"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: `1.5px solid ${CL.goldL}`, borderRadius: 9, fontSize: 14, fontFamily: FONT.body, background: '#FFFEF9', outline: 'none', color: CL.bark }} />
                <div style={{ fontSize: 11, color: CL.stone, marginTop: 4 }}>
                  {lang === 'bg' ? 'Промяната се отразява във всички резервации на този гост.' : "Changes apply to all of this guest's bookings."}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, marginBottom: 6, fontFamily: FONT.body }}>{lang === 'bg' ? 'Предпочитан контакт' : 'Preferred contact'}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {contactOptions.map(opt => {
                    const on = guestEdit.contact === opt.v;
                    return (
                      <button key={opt.v} onClick={() => setGuestEdit(d => ({ ...d, contact: on ? '' : opt.v }))}
                        style={{ padding: '7px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: FONT.body, cursor: 'pointer',
                          border: `1px solid ${on ? CL.olive : CL.goldL}`, background: on ? CL.olive : CL.cream, color: on ? '#fff' : CL.stone }}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={saveGuestEdit} style={{ flex: 1, background: CL.olive, color: '#fff', border: 'none', padding: 12, borderRadius: 10, fontFamily: FONT.body, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{lang === 'bg' ? 'Запази' : 'Save'}</button>
                <button onClick={() => setGuestEdit(null)} style={{ flex: 1, background: CL.cream, color: CL.bark, border: `1px solid ${CL.goldL}`, padding: 12, borderRadius: 10, fontFamily: FONT.body, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{lang === 'bg' ? 'Отказ' : 'Cancel'}</button>
              </div>
              <div style={{ borderTop: `1px solid ${CL.goldL}`, marginTop: 14, paddingTop: 12 }}>
                {g.stays.length === 0 ? (
                  <button onClick={() => setGuestDeleteConfirm(true)} style={{ background: 'none', border: 'none', color: CL.terra, cursor: 'pointer', fontSize: 12, padding: '4px 0', textDecoration: 'underline', textUnderlineOffset: 3, fontFamily: FONT.body }}>
                    {lang === 'bg' ? 'Изтрий този гост' : 'Delete this guest'}
                  </button>
                ) : (
                  <div style={{ fontSize: 11, color: CL.stone, fontFamily: FONT.body }}>
                    {lang === 'bg'
                      ? `Гост може да бъде изтрит само ако няма резервации (този има ${g.stays.length}).`
                      : `A guest can only be deleted with no bookings attached (this one has ${g.stays.length}).`}
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', borderTop: `1px solid ${CL.goldL}` }}>
            {[
              { num: g.stays.length, label: lang === 'bg' ? 'ПРЕСТОЯ' : 'STAYS' },
              { num: `${cur}${g.totalSpend.toLocaleString()}`, label: lang === 'bg' ? 'ПОХАРЧЕНИ' : 'SPENT' },
              { num: totalNights, label: lang === 'bg' ? 'НОЩУВКИ' : 'NIGHTS' },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, padding: '12px 8px', textAlign: 'center', borderLeft: i > 0 ? `1px solid ${CL.goldL}` : 'none' }}>
                <div style={{ fontFamily: FONT.display, fontSize: 22, fontWeight: 500, color: CL.bark, lineHeight: 1.1, fontVariantNumeric: 'lining-nums tabular-nums' }}>{s.num}</div>
                <div style={{ fontSize: 9.5, letterSpacing: 1.4, color: CL.stone, fontWeight: 700, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Surface card — Note + Stay history (hairline-divided sections) */}
        <div style={{ background: CL.cream, margin: '10px 14px', borderRadius: 14, border: `1px solid ${CL.goldL}`, overflow: 'hidden' }}>
          {/* ◆ NOTE */}
          <div style={{ padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Diamond size={5} />
              <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontWeight: 700, fontFamily: FONT.body }}>{lang === 'bg' ? 'Бележка' : 'Note'}</span>
            </div>
            <textarea
              style={{
                width: '100%', boxSizing: 'border-box',
                minHeight: 56, resize: 'vertical',
                padding: '10px 12px',
                border: `1.5px solid ${CL.goldL}`, borderRadius: 9,
                fontSize: 14, fontFamily: FONT.body, background: '#FFFEF9', outline: 'none', color: CL.bark,
              }}
              placeholder={t.guestNotePh}
              value={guestNotes[phoneKey] || ''}
              onChange={e => saveGuestNote(phoneKey, e.target.value)}
            />
            {pref && (
              <div style={{ marginTop: 8, fontSize: 11, color: CL.stone, fontFamily: FONT.body }}>
                <span style={{ letterSpacing: 1.6, textTransform: 'uppercase', fontWeight: 700 }}>{lang === 'bg' ? 'Предпочита' : 'Prefers'}</span>{' '}
                <span style={{ color: CL.bark, fontWeight: 600, textTransform: 'capitalize' }}>{pref}</span>
                <span style={{ color: CL.stone }}> · {guestLang}</span>
              </div>
            )}
          </div>

          {/* ◆ STAY HISTORY */}
          <div style={{ padding: '14px 18px', borderTop: `1px solid ${CL.goldL}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Diamond size={5} />
                <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontWeight: 700, fontFamily: FONT.body }}>{lang === 'bg' ? 'История на престоите' : 'Stay history'}</span>
              </span>
              <span style={{ fontSize: 11, color: CL.stone, fontFamily: FONT.body }}>{g.stays.length} {t.stays}</span>
            </div>
            {g.stays.map((x, i) => {
              const isFuture = x.checkOut > today;
              const isUpcoming = x.checkIn > today;
              const fullBk = bks.find(b => b.id === x.id);
              return (
                <div key={i} onClick={() => fullBk && setViewBk(fullBk)} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  padding: '10px 0', borderTop: i > 0 ? `1px solid ${CL.goldL}` : 'none',
                  cursor: fullBk ? 'pointer' : 'default',
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {isFuture && <span style={{ width: 5, height: 5, borderRadius: '50%', background: CL.olive, flexShrink: 0 }} />}
                      <span style={{ fontFamily: FONT.display, fontSize: 15, fontWeight: 500, color: CL.bark, lineHeight: 1.15, fontVariantNumeric: 'lining-nums tabular-nums' }}>
                        {fmtDateRange(x.checkIn, x.checkOut, lang)}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: CL.stone, marginTop: 2, fontFamily: FONT.body }}>
                      {rName(x.roomId)} · {x.nights} {x.nights === 1 ? t.nt : t.nts}
                      {isUpcoming && <> · <span style={{ color: CL.olive, fontWeight: 600 }}>{lang === 'bg' ? 'предстои' : 'upcoming'}</span></>}
                    </div>
                  </div>
                  <div style={{ fontFamily: FONT.display, fontSize: 16, fontWeight: 500, color: CL.bark, marginLeft: 12, fontVariantNumeric: 'lining-nums tabular-nums', whiteSpace: 'nowrap' }}>{cur}{x.price.toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer — + New booking (bark) + Message (cream) */}
        <div style={{ padding: '12px 14px 16px', background: CL.sand, borderTop: `1px solid ${CL.goldL}`, display: 'flex', gap: 10, paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}>
          <button onClick={startNewFromGuest} style={{
            flex: 1, background: CL.bark, color: CL.gold, border: 'none',
            padding: 14, borderRadius: 11, fontFamily: FONT.body, fontWeight: 600,
            fontSize: 14, letterSpacing: 0.4, cursor: 'pointer',
          }}>+ {lang === 'bg' ? 'Нова резервация' : 'New booking'}</button>
          <button onClick={messageGuest} style={{
            flex: 1, background: CL.cream, color: CL.bark, border: `1px solid ${CL.goldL}`,
            padding: 14, borderRadius: 11, fontFamily: FONT.body, fontWeight: 600,
            fontSize: 14, letterSpacing: 0.4, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>💬 {lang === 'bg' ? 'Съобщение' : 'Message'}</button>
        </div>

        {guestDeleteConfirm && (
          <ConfirmModal
            title={lang === 'bg' ? `Изтрий ${g.name}?` : `Delete ${g.name}?`}
            body={lang === 'bg'
              ? 'Това премахва запазената бележка и предпочитанията за този гост. Не може да се възстанови.'
              : "This removes the saved note and preferences for this guest. It can't be undone."}
            cancelLabel={lang === 'bg' ? 'Отказ' : 'Cancel'}
            confirmLabel={lang === 'bg' ? 'Изтрий' : 'Delete'}
            onCancel={() => setGuestDeleteConfirm(false)}
            onConfirm={deleteGuest}
          />
        )}
      </div>
    );
  }

  // ── BOOKING DETAIL ──
  else if (viewBk) {
    const b = viewBk;
    const room = rooms.find(r => r.id === b.roomId);
    const n = nightCount(b.checkIn, b.checkOut);
    const guest = gDir[cleanPhone(b.phone)];
    const isRep = guest && guest.stays.length > 1;
    const isBcom = b.source === "Booking.com";
    const gr = Number(b.price) || 0;
    const trackComm = property?.track_ota_commission ?? true;
    const cm = (isBcom && trackComm) ? bcomCalc(gr, property?.bcom_commission ?? 15, property?.bcom_vcc_fee_pct ?? 1.5) : null;
    // Single-note model. b.note already falls back to legacy notesCi/notesCo
    // in bookingFromDb during the migration-012 transition window.
    const noteText = b.note || '';
    const sourceIsBcom = isBcom;
    const apartLabel = lang === 'bg' ? 'Апартамент' : 'Apartment';
    const roomLabel = lang === 'bg' ? 'Стая' : 'Room';
    const editLbl = lang === 'bg' ? 'Промени резервация' : 'Edit booking';
    const moveLbl = lang === 'bg' ? '↔ Премести' : '↔ Move';
    const cancelLbl = lang === 'bg' ? 'Анулирай резервация' : 'Cancel booking';
    const noShowLbl = lang === 'bg' ? 'Маркирай неявяване' : 'Mark no-show';
    const deleteLbl = lang === 'bg' ? 'Изтрий резервацията' : 'Delete booking';
    const moreLbl = lang === 'bg' ? 'Още' : 'More';
    const noteHeading = lang === 'bg' ? 'Бележка' : 'Note';
    async function setBookingStatus(newStatus) {
      if (!canEdit) return;
      // No-show / cancelled now also prompts for an amount kept (cancellation
      // fee). Whatever the owner enters becomes the booking's recorded price
      // so it still flows into season revenue. Then the room frees up — these
      // bookings are filtered out of the calendar / today views.
      let priceKept = null;
      if (newStatus === 'no_show' || newStatus === 'cancelled') {
        const promptMsg = lang === 'bg'
          ? `Колко взехте от тази резервация? (€)\n\nОставете 0, ако не сте взели нищо.\nТекуща цена: ${cur}${b.price || 0}`
          : `How much did you keep from this booking? (${cur})\n\nLeave 0 if you charged nothing.\nCurrent price: ${cur}${b.price || 0}`;
        const ans = window.prompt(promptMsg, '0');
        if (ans === null) { setBkKebabOpen(false); return; }  // cancelled
        priceKept = Math.max(0, Number(ans) || 0);
      }
      const patch = priceKept != null
        ? { status: newStatus, price: priceKept }
        : { status: newStatus };
      const { error } = await supabase.from('bookings').update(patch).eq('id', b.id);
      if (error) {
        showToast(localiseError(error, lang));
        setBkKebabOpen(false);
        return;
      }
      setBks(prev => prev.map(x => x.id === b.id ? { ...x, ...patch } : x));
      setViewBk({ ...b, ...patch });
      setBkKebabOpen(false);
    }
    async function deleteBooking() {
      if (!canEdit) return;
      // Permanent hard delete — removes the booking from reports too, and lets a
      // guest with no remaining bookings be deleted. Cancelling (above) keeps the
      // booking; this removes it entirely.
      const confirmMsg = lang === 'bg'
        ? 'Изтриване на тази резервация завинаги?\n\nТя ще изчезне и от отчетите ви. Това не може да бъде отменено.'
        : 'Delete this booking permanently?\n\nIt will also disappear from your reports. This cannot be undone.';
      if (!window.confirm(confirmMsg)) { setBkKebabOpen(false); return; }
      const { error } = await supabase.from('bookings').delete().eq('id', b.id);
      if (error) { showToast(localiseError(error, lang)); setBkKebabOpen(false); return; }
      setBks(prev => prev.filter(x => x.id !== b.id));
      setBkKebabOpen(false);
      setViewBk(null);
      showToast(lang === 'bg' ? 'Резервацията е изтрита' : 'Booking deleted');
    }
    const returningHeading = lang === 'bg' ? `★ Връщащ се · ${guest?.stays?.length || 0} ${t.stays}` : `★ Returning · ${guest?.stays?.length || 0} ${t.stays}`;
    content = (
      <div>
        {/* Drill-down header: back arrow + wordmark */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px 12px", background: CL.cream, position: "sticky", top: 0, zIndex: 50, borderBottom: `1px solid ${HAIRLINE}` }}>
          <button onClick={() => setViewBk(null)} aria-label="Back" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 22, lineHeight: 1, color: CL.bark, width: 22 }}>‹</button>
          <Wordmark onClick={() => { setViewBk(null); setTab('today'); setExpandedSquare(null); }} />
          <span style={{ width: 22 }} />
        </div>

        {/* Single cream surface with hairline-divided sections */}
        <div style={{ background: CL.cream, margin: "10px 14px", borderRadius: 14, border: `1px solid ${CL.goldL}`, overflow: "hidden" }}>
          {/* Hero */}
          <div style={{ padding: "16px 18px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8 }}>
              <div style={{ fontSize: 10, color: isRep ? CL.olive : "transparent", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700 }}>
                {isRep ? returningHeading : "·"}
              </div>
              <span style={{ padding: "3px 9px", fontSize: 10, letterSpacing: 1.4, fontWeight: 700, textTransform: "uppercase", background: sourceIsBcom ? CL.sea : CL.cream, border: `1px solid ${sourceIsBcom ? CL.sea : CL.goldL}`, color: sourceIsBcom ? "#fff" : CL.stone, borderRadius: 5, flexShrink: 0 }}>{displaySource(b.source, lang)}</span>
            </div>
            <div style={{ fontFamily: FONT.display, fontSize: 28, fontWeight: 500, color: CL.bark, margin: "0 0 8px", lineHeight: 1.05 }}>{b.guestName}</div>
            {b.phone && (
              <a href={`tel:${b.phone}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: CL.bark, textDecoration: "none", fontSize: 13.5, fontWeight: 500, marginBottom: 10 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ color: CL.olive, flexShrink: 0 }} aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span style={{ borderBottom: `1px solid ${CL.goldL}`, paddingBottom: 1 }}>{b.phone}</span>
              </a>
            )}
            <div style={{ fontSize: 13, color: CL.bark, lineHeight: 1.5 }}>
              <strong>{roomLabel} {rName(b.roomId)}</strong>
              {isApt(b.roomId) && <span style={{ color: CL.stone }}> · {apartLabel}</span>}
              {' · '}{fmtDateRange(b.checkIn, b.checkOut, lang)} · {n} {n === 1 ? t.nt : t.nts}<br />
              <strong>{cur}{gr.toLocaleString()}</strong> {lang === 'bg' ? 'общо' : 'total'} <span style={{ color: CL.stone }}>· {fmtGuests(b, t)}</span>
              {b.status && b.status !== 'confirmed' && (
                <span style={{ marginLeft: 8, padding: "2px 8px", fontSize: 10, letterSpacing: 1.2, fontWeight: 700, textTransform: "uppercase", background: b.status === 'cancelled' ? CL.terra : CL.stone, color: "#fff", borderRadius: 4 }}>
                  {b.status === 'cancelled' ? (lang === 'bg' ? 'Анулирана' : 'Cancelled') : (lang === 'bg' ? 'Неявяване' : 'No-show')}
                </span>
              )}
            </div>
          </div>

          {/* Payment — pill-group inside the surface */}
          {property && (
            <div style={{ padding: "12px 18px", borderTop: `1px solid ${CL.goldL}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Diamond size={5} />
                <span style={{ fontSize: 10, letterSpacing: 1.6, color: CL.stone, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'bg' ? 'Плащане' : 'Payment'}</span>
              </div>
              <PaymentQuickPick
                booking={b}
                totalPrice={gr}
                cur={cur}
                lang={lang}
                t={t}
                property={property}
                onUpdateBooking={(updated) => {
                  setBks(prev => prev.map(x => x.id === updated.id ? updated : x));
                  setViewBk(updated);
                }}
                showToast={showToastWithUndo}
                inSurface
              />
            </div>
          )}

          {/* Booking.com commission breakdown */}
          {sourceIsBcom && cm && (
            <div style={{ padding: "12px 18px", borderTop: `1px solid ${CL.goldL}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Diamond size={5} />
                <span style={{ fontSize: 10, letterSpacing: 1.6, color: CL.stone, fontWeight: 700, textTransform: "uppercase" }}>{t.bcomNote}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 4, fontSize: 12.5, color: CL.bark }}>
                <div>{t.gross}: <strong>{cur}{gr}</strong></div>
                <div>{t.commission}: <strong>({cur}{cm.comm})</strong></div>
                <div>{t.vccFee}: <strong>({cur}{cm.vcc})</strong></div>
                <div style={{ fontWeight: 700, color: CL.olive }}>{t.net}: {cur}{cm.net}</div>
              </div>
            </div>
          )}

          {/* Stay — combines Note + Extras into one hairline-divided section per mockup */}
          {(noteText || (b.extras && b.extras.length > 0)) && (
            <div style={{ padding: "12px 18px", borderTop: `1px solid ${CL.goldL}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Diamond size={5} />
                <span style={{ fontSize: 10, letterSpacing: 1.6, color: CL.stone, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'bg' ? 'Престой' : 'Stay'}</span>
              </div>
              {noteText && (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "3px 0", fontSize: 13, color: CL.bark, lineHeight: 1.5 }}>
                  <span style={{ color: CL.stone, fontSize: 11, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600, alignSelf: "center", flexShrink: 0 }}>{noteHeading}</span>
                  <span style={{ textAlign: "right" }}>{noteText}</span>
                </div>
              )}
              {b.extras && b.extras.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: noteText ? 8 : 0 }}>
                  {b.extras.map((key, idx) => { const label = extraLabel(key, lang); return label ? <span key={idx} style={{ padding: "4px 10px", borderRadius: 999, fontSize: 11.5, fontWeight: 600, color: CL.bark, background: CL.cream, border: `1px solid ${CL.goldL}` }}>{label}</span> : null; })}
                </div>
              )}
            </div>
          )}

          {/* Messages — CommPanel rendered as a section inside the surface */}
          {property && room && (
            <div style={{ padding: "12px 18px", borderTop: `1px solid ${CL.goldL}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Diamond size={5} />
                <span style={{ fontSize: 10, letterSpacing: 1.6, color: CL.stone, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'bg' ? 'Съобщения' : 'Messages'}</span>
              </div>
              <CommPanel
                booking={b}
                room={room}
                property={property}
                lang={lang}
                guestContactPrefs={guestContactPrefs}
                guestTrusted={guestTrusted}
                onUpdateBooking={(updated) => {
                  setBks(prev => prev.map(x => x.id === updated.id ? updated : x));
                  setViewBk(updated);
                }}
                showToast={showToastWithUndo}
                inSurface
              />
            </div>
          )}

          {/* Repeat-guest pill — opens guest profile */}
          {isRep && (
            <div onClick={() => { setViewBk(null); setViewGuest(guest); }} style={{ padding: "12px 18px", borderTop: `1px solid ${CL.goldL}`, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: CL.olive, fontWeight: 600 }}>★ {guest.stays.length} {t.stays} · {cur}{guest.totalSpend.toLocaleString()}</span>
              <span style={{ color: CL.stone, fontSize: 14 }}>›</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", gap: 8, padding: "14px 14px 22px", background: CL.sand, borderTop: `1px solid ${CL.goldL}` }}>
          <button onClick={() => startEdit(b)} disabled={!canEdit} style={{ flex: 2, background: CL.bark, color: CL.cream, border: "none", padding: 13, borderRadius: 11, fontWeight: 600, fontSize: 13.5, letterSpacing: 0.3, cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.5, fontFamily: FONT.body }}>{editLbl}</button>
          <button onClick={() => startMove(b)} disabled={!canEdit} style={{ flex: 1, background: CL.cream, color: CL.bark, border: `1px solid ${CL.goldL}`, padding: 13, borderRadius: 11, fontWeight: 600, fontSize: 12.5, cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.5, fontFamily: FONT.body, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>{moveLbl}</button>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setBkKebabOpen(o => !o)}
              aria-label={moreLbl}
              aria-expanded={bkKebabOpen}
              style={{ width: 46, height: "100%", background: CL.cream, color: CL.bark, border: `1px solid ${CL.goldL}`, padding: 13, borderRadius: 11, fontFamily: FONT.display, fontSize: 20, fontWeight: 500, cursor: "pointer", lineHeight: 1 }}
            >⋯</button>
            {bkKebabOpen && (
              <>
                <div onClick={() => setBkKebabOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
                <div role="menu" style={{
                  position: "absolute", right: 0, bottom: "calc(100% + 6px)",
                  background: CL.cream, border: `1px solid ${CL.goldL}`, borderRadius: 10,
                  boxShadow: "0 6px 20px rgba(61,43,31,0.18)", minWidth: 180,
                  padding: 4, zIndex: 91,
                }}>
                  {b.status !== 'cancelled' && b.status !== 'no_show' && (
                    <>
                      <button onClick={() => setBookingStatus('cancelled')} role="menuitem" style={{
                        display: "block", width: "100%", textAlign: "left",
                        padding: "10px 12px", border: "none", background: "none",
                        color: CL.terra, fontSize: 13, fontWeight: 600, fontFamily: FONT.body, cursor: "pointer",
                        borderRadius: 6,
                      }}>{cancelLbl}</button>
                      <button onClick={() => setBookingStatus('no_show')} role="menuitem" style={{
                        display: "block", width: "100%", textAlign: "left",
                        padding: "10px 12px", border: "none", background: "none",
                        color: CL.terra, fontSize: 13, fontWeight: 600, fontFamily: FONT.body, cursor: "pointer",
                        borderRadius: 6,
                      }}>{noShowLbl}</button>
                      <div style={{ height: 1, background: CL.goldL, margin: "4px 8px" }} />
                    </>
                  )}
                  <button onClick={deleteBooking} role="menuitem" style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "10px 12px", border: "none", background: "none",
                    color: CL.terra, fontSize: 13, fontWeight: 700, fontFamily: FONT.body, cursor: "pointer",
                    borderRadius: 6,
                  }}>{deleteLbl}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── FORM ──
  else if (tab === "form" && formInit) {
    content = <BkForm initial={formInit} rooms={rooms} allBk={bks} gDir={gDir} guestNotes={guestNotes} guestTrusted={guestTrusted} onSave={onFormSave} onCancel={() => { setFormInit(null); setTab("today"); }} showToast={showToast} rName={rName} t={t} lang={lang} commissionPct={property?.bcom_commission ?? 15} extras={property?.extras ?? []} sources={property?.sources} trackOtaCommission={property?.track_ota_commission ?? true} cur={cur} property={property} />;
  }

  // ── TODAY ──
  else if (tab === "today") {
    const todayDate = new Date();
    const locale = lang === 'bg' ? 'bg-BG' : 'en-GB';
    // Build "Weekday, D Month" explicitly so the comma is consistent across browsers
    // (Chromium en-GB toLocaleDateString omits the comma in some versions).
    const weekday = todayDate.toLocaleDateString(locale, { weekday: 'long' });
    const dayMonth = todayDate.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    const dateHeading = `${weekday}, ${dayMonth}`;
    const dateHeadingCap = dateHeading.charAt(0).toUpperCase() + dateHeading.slice(1);
    // Use the uncapped count for the badge — buildTodayFeed caps the *list* at 10 for
     // UI sanity, but the square should show the true number of actionable messages.
     const messagesCount = property ? countTodayActions(bks, property, today, guestTrusted) : 0;
    // Two-line English labels (mockup wraps via <br/>); BG single-word labels stay on one line.
    const sqLabels = lang === 'bg'
      ? { requests: 'Заявки', messages: 'Съобщения' }
      : { requests: 'Booking\nRequests', messages: 'Messages\nto send' };
    // tone: 'urgent' (terra outline + cream-orange fill) | 'normal' (goldL outline) | 'calm' (paler hairline outline)
    // Arrivals & departures are shown in full further down, so they are not squares.
    const squares = [];
    if (bookingRequests.length > 0) squares.push({ key: 'requests', count: bookingRequests.length, label: sqLabels.requests, tone: 'urgent' });
    if (messagesCount > 0)          squares.push({ key: 'messages', count: messagesCount, label: sqLabels.messages, tone: 'urgent' });
    const compactSquares = squares.length === 3; // 3-square row uses tighter padding/sizing per mockup

    // Detail-panel labels (used by SectionHeader inside the expanded square panel).
    const detailLbl = {
      requests:  lang === 'bg' ? 'Нови заявки' : 'Booking requests',
      messages:  lang === 'bg' ? 'Съобщения за изпращане' : 'Messages to send',
    };
    const closeLbl = lang === 'bg' ? 'Затвори' : 'Close';
    const sqClick = (key) => setExpandedSquare(prev => prev === key ? null : key);

    // Look up the count for the active square so the header reads "Booking requests · 2".
    const activeSq = squares.find(s => s.key === expandedSquare);

    content = (
      <div>
        <TopBar onMenuClick={() => setDrawerOpen(true)} onLogoClick={() => { setTab('today'); setExpandedSquare(null); }} property={property} lang={lang} onTrialPillClick={() => setShowTrialModal(true)} />

        {!welcomeDismissed && property && bks.length === 0 && (
          <WelcomeBanner lang={lang} onDismiss={dismissWelcome} />
        )}

        <div style={{ padding: "22px 18px 22px" }}>
          <button
            type="button"
            onClick={() => setTab('occupancy')}
            aria-label={lang === 'bg' ? 'Отвори календара' : 'Open calendar'}
            style={{
              background: "none", border: "none", padding: 0, margin: "0 0 4px", cursor: "pointer",
              display: "flex", alignItems: "baseline", gap: 10, textAlign: "left",
              fontFamily: FONT.display, fontSize: 26, fontWeight: 500, color: CL.bark, lineHeight: 1.1,
            }}
          >
            <span>{dateHeadingCap}</span>
            <span aria-hidden="true" style={{ fontFamily: FONT.display, fontSize: 22, color: CL.gold, lineHeight: 1 }}>›</span>
          </button>
          {property?.name && (
            <div style={{ fontSize: 10, color: CL.stone, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 500 }}>{property.name}</div>
          )}
        </div>

        {squares.length > 0 && (
          <div style={{ padding: "0 18px 14px", display: "grid", gridTemplateColumns: compactSquares ? "repeat(3, 1fr)" : "1fr 1fr", gap: compactSquares ? 8 : 10 }}>
            {squares.map(sq => {
              const isUrgent = sq.tone === 'urgent';
              const isActive = expandedSquare === sq.key;
              const borderColor = sq.tone === 'urgent' ? CL.terra : sq.tone === 'calm' ? HAIRLINE : CL.goldL;
              const bgColor = isUrgent ? "#FFF5F0" : CL.cream;
              const numColor = isUrgent ? CL.terra : CL.bark;
              // Mockup: only the number turns terra on urgent — labels stay stone for both tones.
              const labelColor = CL.stone;
              return (
                <button key={sq.key} data-sq={sq.key} data-testid={sq.key === 'requests' ? 'booking-requests-tab' : undefined} data-tone={sq.tone} data-active={isActive ? "1" : "0"} onClick={() => sqClick(sq.key)} style={{
                  position: "relative",
                  background: bgColor,
                  // Active square gets a slightly thicker bark outline so the user can see what's expanded below.
                  border: isActive ? `1.5px solid ${CL.bark}` : `1px solid ${borderColor}`,
                  borderRadius: 10,
                  padding: compactSquares ? "12px 8px" : "14px 10px",
                  cursor: "pointer", textAlign: "left",
                  fontFamily: FONT.body, minWidth: 0,
                }}>
                  <span style={{
                    position: "absolute",
                    top: compactSquares ? 10 : 14, right: compactSquares ? 8 : 12,
                    fontFamily: FONT.display, fontSize: compactSquares ? 14 : 18, color: CL.gold, lineHeight: 1,
                    transform: isActive ? "rotate(90deg)" : "none", transformOrigin: "center", transition: "transform 0.15s",
                  }}>›</span>
                  <span style={{ display: "block", fontFamily: FONT.display, fontWeight: 500, fontSize: compactSquares ? 26 : 32, color: numColor, lineHeight: 1, fontVariantNumeric: "lining-nums tabular-nums" }}>{sq.count}</span>
                  <span style={{
                    display: "block", marginTop: 8, whiteSpace: "pre-line",
                    fontSize: compactSquares ? 8.5 : 9, fontWeight: 500,
                    letterSpacing: compactSquares ? "0.12em" : "0.16em",
                    color: labelColor, textTransform: "uppercase", lineHeight: 1.3,
                  }}>{sq.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Always-on summary boxes — occupancy, arrivals, departures */}
        <div style={{ padding: "0 18px 14px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {[
            { num: `${occIds.size}/${rooms.length}`, label: lang === 'bg' ? 'Заетост' : 'Occupancy' },
            { num: todayIn.length,  label: lang === 'bg' ? 'Пристигат' : 'Arrivals' },
            { num: todayOut.length, label: lang === 'bg' ? 'Заминават' : 'Departures' },
          ].map((s, i) => (
            <div key={i} style={{ background: CL.cream, border: `1px solid ${CL.goldL}`, borderRadius: 10, padding: "12px 8px", minWidth: 0 }}>
              <span style={{ display: "block", fontFamily: FONT.display, fontWeight: 500, fontSize: 26, color: CL.bark, lineHeight: 1, fontVariantNumeric: "lining-nums tabular-nums" }}>{s.num}</span>
              <span style={{ display: "block", marginTop: 8, fontSize: 8.5, fontWeight: 500, letterSpacing: "0.12em", color: CL.stone, textTransform: "uppercase", lineHeight: 1.3 }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Expanded detail panel — only renders when a square is tapped. Header has × close on the right. */}
        {activeSq && (
          <div style={{ marginBottom: 18 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              margin: "18px 14px 10px",
              fontFamily: FONT.display, fontWeight: 500, fontSize: 16, color: CL.bark, lineHeight: 1.1,
            }}>
              <span style={{ width: 8, height: 1, background: CL.goldL, flexShrink: 0 }} />
              <span>{detailLbl[expandedSquare]} · {activeSq.count}</span>
              <span style={{ flex: 1, height: 1, background: CL.goldL }} />
              <button onClick={() => setExpandedSquare(null)} aria-label={closeLbl} style={{
                background: "none", border: "none", color: CL.stone, fontFamily: FONT.display,
                fontSize: 20, lineHeight: 1, cursor: "pointer", padding: "0 0 0 6px",
              }}>×</button>
            </div>

            {expandedSquare === 'messages' && (
              <TodayActionsFeed
                bookings={bks} rooms={rooms} property={property} lang={lang}
                guestContactPrefs={guestContactPrefs} guestTrusted={guestTrusted}
                onOpenBooking={(b) => setViewBk(b)}
                onUpdateBooking={(updated) => { setBks(prev => prev.map(x => x.id === updated.id ? updated : x)); }}
                showToastWithUndo={showToastWithUndo}
                hideHeader
              />
            )}

            {expandedSquare === 'requests' && (
              <div data-testid="booking-requests-pending" style={{ margin: '0 14px' }}>
                {bookingRequests.map(req => (
                  <div key={req.id} style={{ background: CL.cream, border: `1px solid ${CL.sea}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: CL.bark }}>{req.guest_name}</div>
                        <div style={{ fontSize: 12, color: CL.stone, marginTop: 2 }}>
                          {req.check_in} → {req.check_out} · {req.adults} adult{req.adults !== 1 ? 's' : ''}
                          {req.children?.length > 0 ? ` · ${req.children.length} child${req.children.length !== 1 ? 'ren' : ''}` : ''}
                          {(req.rooms_requested?.length ?? 0) > 1 ? ` · ${req.rooms_requested.length} rooms` : ''}
                        </div>
                        <div style={{ fontSize: 11, color: CL.sea, marginTop: 2, fontWeight: 600, textTransform: 'capitalize' }}>
                          via {req.preferred_contact} · {req.phone || req.email}
                        </div>
                        {req.message && <div style={{ fontSize: 12, color: CL.stone, marginTop: 4, fontStyle: 'italic' }}>"{req.message}"</div>}
                      </div>
                    </div>
                    {(() => {
                      const isMultiRoom = (req.rooms_requested?.length ?? 0) > 1;
                      if (!isMultiRoom) {
                        return (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button data-testid="approve-request-btn" onClick={() => startFromRequest(req)}
                              style={{ flex: 1, padding: '8px 0', background: CL.olive, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                              {lang === 'bg' ? 'Потвърди' : 'Approve'}
                            </button>
                            <button onClick={() => declineRequest(req)}
                              style={{ flex: 1, padding: '8px 0', background: CL.cream, color: CL.terra, border: `1px solid ${CL.terra}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                              {lang === 'bg' ? 'Откажи' : 'Decline'}
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div>
                          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                            {req.rooms_requested.map((_, i) => {
                              const done = i < (req.rooms_assigned || 0);
                              const active = i === (req.rooms_assigned || 0);
                              return (
                                <button key={i}
                                  onClick={() => { if (active) startRoomFromRequest(req, i); }}
                                  disabled={!active && !done}
                                  style={{
                                    flex: 1, padding: '7px 0',
                                    background: done ? CL.olive : active ? CL.sea : CL.goldL,
                                    color: done || active ? '#fff' : CL.stone,
                                    border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700,
                                    cursor: active ? 'pointer' : 'default',
                                    opacity: !done && !active ? 0.6 : 1,
                                  }}>
                                  {done ? '✓' : `${lang === 'bg' ? 'Стая' : 'Room'} ${i + 1}`}
                                </button>
                              );
                            })}
                          </div>
                          <button onClick={() => declineRequest(req)}
                            style={{ width: '100%', padding: '8px 0', background: CL.cream, color: CL.terra, border: `1px solid ${CL.terra}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            {lang === 'bg' ? 'Откажи' : 'Decline'}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                ))}
                {pastRequests.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <button onClick={() => setShowRequestHistory(h => !h)}
                      style={{ background: 'none', border: 'none', padding: '4px 0', fontSize: 12, color: CL.stone, cursor: 'pointer', fontWeight: 600 }}>
                      {showRequestHistory
                        ? (lang === 'bg' ? '▲ Скрий историята' : '▲ Hide request history')
                        : (lang === 'bg' ? `▼ ${pastRequests.length} предишни заявки` : `▼ ${pastRequests.length} past request${pastRequests.length !== 1 ? 's' : ''}`)}
                    </button>
                    {showRequestHistory && pastRequests.map(req => (
                      <div key={req.id} style={{ background: CL.sand, border: `1px solid ${CL.goldL}`, borderRadius: 12, padding: '10px 14px', marginBottom: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: CL.bark }}>{req.guest_name}</div>
                            <div style={{ fontSize: 12, color: CL.stone, marginTop: 2 }}>
                              {req.check_in} → {req.check_out} · {req.adults} adult{req.adults !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: req.status === 'approved' ? CL.olive : CL.terra, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {req.status === 'approved' ? (lang === 'bg' ? 'Потвърдена' : 'Approved') : (lang === 'bg' ? 'Отказана' : 'Declined')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        )}

        {/* Arriving today — always shown in full when there are arrivals */}
        {todayIn.length > 0 && (
          <div style={{ marginBottom: 4 }}>
            <SectionHeader>{(lang === 'bg' ? 'Пристигат днес' : 'Arriving today')} · {todayIn.length}</SectionHeader>
            <div style={{ margin: "0 14px" }}>
              {todayIn.map(b => renderRow(b, () => setViewBk(b), "ci"))}
            </div>
          </div>
        )}

        {/* Departing today — always shown in full when there are departures */}
        {todayOut.length > 0 && (
          <div style={{ marginBottom: 4 }}>
            <SectionHeader>{(lang === 'bg' ? 'Заминават днес' : 'Departing today')} · {todayOut.length}</SectionHeader>
            <div style={{ margin: "0 14px" }}>
              {todayOut.map(b => renderRow(b, () => setViewBk(b), "co"))}
            </div>
          </div>
        )}

        {/* In-house list — compact rows matching the mockup vocabulary:
            room-num pill · name + phone meta · right-aligned nights remaining. */}
        {curGuests.length > 0 && (
          <div id="sec-inhouse">
            <SectionHeader>{t.inHouse} · {curGuests.length}</SectionHeader>
            <div style={{ margin: "0 14px" }}>
              {curGuests.map(b => {
                const nightsLeft = Math.max(0, nightCount(today, b.checkOut));
                const leavesToday = nightsLeft === 0;
                const leavesTomorrow = nightsLeft === 1;
                const nightsBig = leavesToday || leavesTomorrow
                  ? (lang === 'bg' ? 'заминава' : 'leaves')
                  : String(nightsLeft);
                const nightsSub = leavesToday
                  ? (lang === 'bg' ? 'днес' : 'today')
                  : leavesTomorrow
                    ? (lang === 'bg' ? 'утре' : 'tomorrow')
                    : (lang === 'bg' ? 'нощувки' : nightsLeft === 1 ? 'night left' : 'nights left');
                return (
                  <div key={b.id} onClick={() => setViewBk(b)}
                    style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 4px", borderBottom: `1px solid ${CL.sand}`, cursor: "pointer", fontSize: 12 }}>
                    <div style={{
                      width: 28, height: 28, border: `1px solid ${CL.goldL}`, borderRadius: 6, background: CL.cream,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: FONT.display, fontSize: 14, color: CL.bark, flexShrink: 0,
                    }}>{rName(b.roomId)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: CL.bark, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {b.guestName}
                      </div>
                      {b.phone && (
                        <div style={{ fontSize: 10, color: CL.stone, marginTop: 2 }}>{b.phone}</div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", fontSize: 10, color: CL.stone, letterSpacing: "0.06em", flexShrink: 0 }}>
                      <span style={{ display: "block", fontFamily: FONT.display, fontSize: 14, color: CL.bark }}>{nightsBig}</span>
                      {nightsSub}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── OCCUPANCY ──
  else if (tab === "occupancy") {
    const isWeekView = occView === "week";
    let visibleDates = [];

    if (isWeekView) {
      const ws = new Date(weekStart + "T12:00:00");
      for (let i = 0; i < 7; i++) {
        const d = new Date(ws);
        d.setDate(d.getDate() + i);
        visibleDates.push(isoToday(d));
      }
    } else {
      for (let d = 1; d <= daysInMonth; d++) {
        visibleDates.push(isoMake(cY, cM + 1, d));
      }
    }

    const numDays = visibleDates.length;
    const activeRooms = rooms.filter(r => !r.archived);
    const activeRoomIds = new Set(activeRooms.map(r => r.id));
    const dailyOcc = visibleDates.map(ds => new Set(bks.filter(b => isLive(b) && activeRoomIds.has(b.roomId) && b.checkIn <= ds && b.checkOut > ds).map(b => b.roomId)).size);
    const bookedTotal = dailyOcc.reduce((s, c) => s + c, 0);
    const totalRN = activeRooms.length * numDays;
    const occPct = totalRN > 0 ? Math.round(bookedTotal / totalRN * 100) : 0;

    const roomGaps = !isWeekView ? activeRooms.map(rm => {
      const gaps = [];
      let gs = null;
      for (let d = 1; d <= daysInMonth; d++) {
        const ds = isoMake(cY, cM + 1, d);
        const has = bks.some(b => b.roomId === rm.id && b.checkIn <= ds && b.checkOut > ds);
        if (!has && gs === null) gs = d;
        if ((has || d === daysInMonth) && gs !== null) { const ge = has ? d - 1 : d; if (ge >= gs) gaps.push({ f: gs, t: ge, n: ge - gs + 1 }); gs = null; }
      }
      return { room: rm, gaps };
    }).filter(x => x.gaps.length > 0) : [];

    function shiftWeek(dir) {
      const d = new Date(weekStart + "T12:00:00");
      d.setDate(d.getDate() + dir * 7);
      setWeekStart(isoToday(d));
    }

    function headerDate(ds) {
      const d = new Date(ds + "T12:00:00");
      return { day: d.getDate(), dow: t.days[(d.getDay() + 6) % 7], wk: d.getDay() === 0 || d.getDay() === 6 };
    }

    const weekLabel = isWeekView ? (() => {
      const f = new Date(visibleDates[0] + "T12:00:00");
      const l = new Date(visibleDates[6] + "T12:00:00");
      const fM = t.months[f.getMonth()];
      const lM = t.months[l.getMonth()];
      const fD = f.getDate();
      const lD = l.getDate();
      const fY = f.getFullYear();
      const lY = l.getFullYear();
      if (fY !== lY)         return `${fM} ${fD}, ${fY} – ${lM} ${lD}, ${lY}`;
      if (fM !== lM)         return `${fM} ${fD} – ${lM} ${lD}, ${lY}`;
      return `${fM} ${fD} – ${lD}, ${lY}`;
    })() : "";

    const calTitleLbl = lang === 'bg' ? 'Календар' : 'Calendar';
    const arrowBtn = (dir, onClick) => (
      <button onClick={onClick} aria-label={dir === '‹' ? 'Previous' : 'Next'} style={{
        width: 28, height: 28, border: `1px solid ${CL.goldL}`, borderRadius: "50%",
        background: "none", display: "flex", alignItems: "center", justifyContent: "center",
        color: CL.olive, fontFamily: FONT.display, fontSize: 18, lineHeight: 1, cursor: "pointer", padding: 0,
      }}>{dir}</button>
    );
    content = (
      <div>
        <TopBar onMenuClick={() => setDrawerOpen(true)} onLogoClick={() => { setTab('today'); setExpandedSquare(null); }} property={property} lang={lang} onTrialPillClick={() => setShowTrialModal(true)} />

        <div style={{ padding: "14px 16px 8px" }}>
          <div style={{ fontFamily: FONT.display, fontWeight: 500, fontSize: 22, color: CL.bark, lineHeight: 1.1, marginBottom: 10 }}>{calTitleLbl}</div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 18, marginBottom: 10 }}>
            {arrowBtn('‹', () => isWeekView ? shiftWeek(-1) : setCalMonth(cM === 0 ? [cY - 1, 11] : [cY, cM - 1]))}
            <div style={{ fontFamily: FONT.display, fontSize: 20, fontWeight: 500, color: CL.bark, lineHeight: 1, fontVariantNumeric: "lining-nums tabular-nums" }}>
              {isWeekView ? weekLabel : t.months[cM] + " " + cY}
            </div>
            {arrowBtn('›', () => isWeekView ? shiftWeek(1) : setCalMonth(cM === 11 ? [cY + 1, 0] : [cY, cM + 1]))}
          </div>

          <div style={{ display: "flex", background: CL.sand, borderRadius: 7, padding: 3 }}>
            {[
              { key: 'month', label: t.viewMonth, on: !isWeekView },
              { key: 'week',  label: t.viewWeek,  on: isWeekView },
            ].map(opt => (
              <button key={opt.key} onClick={() => setOccView(opt.key)} style={{
                flex: 1, padding: "6px 0", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                fontWeight: 500, border: "none", borderRadius: 5, cursor: "pointer",
                background: opt.on ? CL.cream : "transparent",
                color: opt.on ? CL.olive : CL.stone,
                boxShadow: opt.on ? "0 1px 2px rgba(0,0,0,0.04)" : "none",
              }}>{opt.label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, padding: "0 16px 8px" }}>
          {[
            { n: bookedTotal,        l: t.booked, color: CL.olive },
            { n: totalRN - bookedTotal, l: t.gaps,   color: CL.terra },
            { n: occPct + '%',       l: lang === 'bg' ? 'Заетост' : 'Occupancy', color: CL.sea },
          ].map((s, i) => (
            <div key={i} style={{ background: CL.cream, border: `1px solid ${HAIRLINE}`, borderRadius: 7, padding: "6px 4px", textAlign: "center" }}>
              <div style={{ fontFamily: FONT.display, fontWeight: 500, fontSize: 18, lineHeight: 1, color: s.color }}>{s.n}</div>
              <div style={{ fontSize: 8, letterSpacing: "0.12em", textTransform: "uppercase", color: CL.stone, marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>

        <div style={{ overflowX: isWeekView ? "hidden" : "auto", WebkitOverflowScrolling: "touch" }}>
          <div style={{ ...(isWeekView ? { width: "100%" } : { display: "inline-block", minWidth: scr.labelW + numDays * scr.cellW }), background: HAIRLINE }}>
            <div style={{ display: isWeekView ? "flex" : "grid", gridTemplateColumns: !isWeekView ? `${scr.labelW}px repeat(${numDays}, ${scr.cellW}px)` : undefined, position: "sticky", top: 0, zIndex: 10, background: CL.sand, borderBottom: `1px solid ${HAIRLINE}` }}>
              <div style={{ width: isWeekView ? 50 : scr.labelW, minWidth: isWeekView ? 50 : scr.labelW, padding: 4, fontSize: 9, fontWeight: 700, color: CL.stone, letterSpacing: "0.16em", textTransform: "uppercase", flexShrink: 0, boxSizing: "border-box", boxShadow: "inset -1px 0 0 rgba(0, 0, 0, 0.06)" }}>{t.room}</div>
              {visibleDates.map((ds, i) => {
                const h = headerDate(ds);
                const td = ds === today;
                return (
                  <div key={i} style={{
                    flex: isWeekView ? 1 : "none",
                    width: isWeekView ? "auto" : scr.cellW,
                    minWidth: isWeekView ? 0 : scr.cellW,
                    flexShrink: 0,
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    padding: "4px 0",
                    boxShadow: "inset -1px 0 0 rgba(0, 0, 0, 0.06)",
                  }}>
                    <div style={{ fontFamily: FONT.body, fontSize: 9, color: h.wk ? CL.gold : CL.stone, fontWeight: 600, lineHeight: 1.1 }}>{h.dow}</div>
                    <div style={{
                      fontFamily: FONT.display, fontSize: 13, fontWeight: td ? 700 : 500,
                      color: td ? CL.olive : CL.bark,
                      background: td ? CL.cream : "transparent",
                      borderRadius: 4, padding: td ? "1px 6px" : "1px 0",
                      marginTop: 2, lineHeight: 1,
                    }}>{h.day}</div>
                  </div>
                );
              })}
            </div>

            {(() => {
              const lastVis = visibleDates[numDays - 1];
              const dayAfterLast = (() => { const x = new Date(lastVis + "T12:00:00"); x.setDate(x.getDate() + 1); return isoToday(x); })();
              return activeRooms.map(rm => {
                const dayCells = visibleDates.map((ds, i) => {
                  const bk = bks.find(b => isLive(b) && b.roomId === rm.id && b.checkIn <= ds && b.checkOut > ds);
                  if (!bk) return { bk: null };
                  const isStart = bk.checkIn === ds;
                  const isFirstVisible = isStart || (i === 0 && bk.checkIn < ds);
                  let spanFromHere = 0;
                  if (isFirstVisible) {
                    for (let j = i; j < numDays; j++) {
                      if (bk.checkIn <= visibleDates[j] && bk.checkOut > visibleDates[j]) spanFromHere++;
                      else break;
                    }
                  }
                  const nextDs = i < numDays - 1 ? visibleDates[i + 1] : (() => { const x = new Date(ds + "T12:00:00"); x.setDate(x.getDate() + 1); return isoToday(x); })();
                  const isEnd = nextDs >= bk.checkOut;
                  return { bk, isStart, isFirstVisible, isEnd, spanFromHere };
                });

                const isApart = isApt(rm.id);
                return (
                  <div key={rm.id} style={{ display: isWeekView ? "flex" : "grid", gridTemplateColumns: !isWeekView ? `${scr.labelW}px repeat(${numDays}, ${scr.cellW}px)` : undefined, borderBottom: `1px solid ${HAIRLINE}` }}>
                    <div style={{ width: isWeekView ? 50 : scr.labelW, minWidth: isWeekView ? 50 : scr.labelW, padding: isWeekView ? "5px 4px" : "0 8px", flexShrink: 0, background: isApart ? CL.apt : CL.cream, display: "flex", alignItems: "center", gap: 4, height: isWeekView ? 38 : scr.isTablet ? 34 : 28, boxSizing: "border-box", boxShadow: "inset -1px 0 0 rgba(0, 0, 0, 0.06)" }}>
                      <span style={{ fontFamily: FONT.display, fontSize: isWeekView ? 14 : 13, fontWeight: 500, color: isApart ? CL.olive : CL.bark, lineHeight: 1 }}>{rm.name}</span>
                      {isApart && <span style={{ fontFamily: FONT.body, fontSize: 7, fontWeight: 700, letterSpacing: "0.08em", color: CL.gold, background: CL.cream, border: `1px solid ${CL.goldL}`, borderRadius: 2, padding: "1px 3px", lineHeight: 1, textTransform: "uppercase", flexShrink: 0 }}>APT</span>}
                    </div>
                    {dayCells.map((cell, i) => {
                      const { bk, isStart, isFirstVisible, isEnd, spanFromHere } = cell;
                      const isBC = bk && bk.source === "Booking.com";
                      const fromPrev = isFirstVisible && !isStart;
                      const intoNext = isFirstVisible && bk && bk.checkOut > dayAfterLast;
                      const cellH = isWeekView ? 36 : scr.isTablet ? 34 : 28;

                      const barBg = bk
                        ? (isBC ? CL.sea : CL.olive)
                        : (isApart ? CL.apt : CL.cream);
                      const barFg = "#fff";
                      // Booking-starts left edge doubles as the payment-status indicator.
                      // Rounded left corner alone signals "starts here"; the colour adds payment state.
                      const payEdge = bk && (bk.paymentStatus === "Paid" ? "#2D5A3E" : bk.paymentStatus === "Deposit" ? "#B8860B" : CL.terra);

                      return (
                        <div key={i}
                          onClick={() => { if (bk) setViewBk(bk); }}
                          style={{
                            flex: isWeekView ? 1 : "none",
                            width: isWeekView ? "auto" : scr.cellW,
                            minWidth: isWeekView ? 0 : scr.cellW,
                            boxSizing: "border-box",
                            height: cellH, flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: bk ? "pointer" : "default",
                            background: barBg,
                            boxShadow: isStart
                              ? `inset 4px 0 0 ${payEdge}, inset -1px 0 0 rgba(0, 0, 0, 0.06)`
                              : "inset -1px 0 0 rgba(0, 0, 0, 0.06)",
                            borderRadius: isStart && isEnd ? "4px" : isStart ? "4px 0 0 4px" : isEnd ? "0 4px 4px 0" : 0,
                            position: "relative",
                            overflow: isFirstVisible ? "visible" : "hidden",
                            zIndex: isFirstVisible ? 5 : 1,
                          }}
                        >
                          {isFirstVisible && (
                            <span style={{
                              position: "absolute", left: isStart ? 8 : 4, top: "50%", transform: "translateY(-50%)",
                              fontFamily: FONT.body,
                              fontSize: isWeekView ? 10 : 8, color: barFg, fontWeight: 700,
                              whiteSpace: "nowrap", pointerEvents: "none",
                              maxWidth: spanFromHere > 1 ? "none" : (isWeekView ? 38 : 24),
                              overflow: spanFromHere <= 1 ? "hidden" : "visible",
                              textOverflow: spanFromHere <= 1 ? "ellipsis" : "clip",
                            }}>
                              {fromPrev ? "← " : ""}{bk.guestName}{multiBookingLabels[bk.id] ? ` (${multiBookingLabels[bk.id]})` : ""}{intoNext ? " →" : ""}
                            </span>
                          )}
                          {isStart && (bk.note || bk.notesCi || bk.notesCo) && <span style={{ position: "absolute", top: 4, right: 4, width: 5, height: 5, background: CL.gold, transform: "rotate(45deg)", pointerEvents: "none" }} />}
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Legend — single-line key for the calendar's bar fills, status, and accents.
            Drops the Gap Opportunities section that previously lived here per user feedback. */}
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 12,
          padding: "8px 14px",
          borderTop: `1px solid ${HAIRLINE}`,
          background: CL.cream,
          fontSize: 8.5, color: CL.stone, fontWeight: 500,
          letterSpacing: "0.06em", textTransform: "uppercase",
        }}>
          {[
            { sw: 'block', color: CL.olive,  label: lang === 'bg' ? 'Потвърдена' : 'Confirmed' },
            { sw: 'block', color: CL.sea,    label: 'Booking.com' },
            { sw: 'edge',  color: CL.terra,  label: lang === 'bg' ? 'Неплатена' : 'Unpaid' },
            { sw: 'edge',  color: '#B8860B', label: lang === 'bg' ? 'Депозит' : 'Deposit' },
            { sw: 'edge',  color: '#2D5A3E', label: lang === 'bg' ? 'Платена' : 'Paid' },
          ].map((it, i) => {
            const swatch = it.sw === 'edge'
              ? <span style={{ width: 14, height: 10, borderRadius: 2, background: CL.olive, boxShadow: `inset 4px 0 0 ${it.color}` }} />
              : <span style={{ width: 10, height: 10, borderRadius: 2, background: it.color }} />;
            return (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                {swatch}{it.label}
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  // ── DIRECTORY ──
  else if (tab === "directory") {
    const filtered = guestSearch ? gList.filter(g => g.name.toLowerCase().includes(guestSearch.toLowerCase()) || g.phone.includes(guestSearch)) : gList;
    const repeats = filtered.filter(g => g.stays.length >= 2).sort((a, b) => b.stays.length - a.stays.length);
    const singles = filtered.filter(g => g.stays.length === 1).sort((a, b) => a.name.localeCompare(b.name));

    // Row atom — Cormorant initial avatar (gold border for returning, neutral cream for first-time).
    function GuestRow({ g, returning }) {
      const meta = returning
        ? <span><span style={{ color: CL.olive, fontWeight: 600 }}>{g.stays.length} {t.stays}</span> · {cur}{g.totalSpend.toLocaleString()}</span>
        : <span style={{ color: CL.stone }}>{g.phone} · {cur}{g.totalSpend.toLocaleString()}</span>;
      return (
        <button key={g.phone} onClick={() => setViewGuest(g)} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          background: CL.cream, border: `1px solid ${CL.goldL}`, borderRadius: 12,
          padding: '12px 14px', margin: '8px 14px', boxSizing: 'border-box',
          cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
          width: 'calc(100% - 28px)',
        }}>
          <span style={{
            width: 36, height: 36, borderRadius: '50%',
            border: `1px solid ${returning ? CL.gold : CL.goldL}`,
            background: CL.cream, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: FONT.display, fontSize: 18, fontWeight: 500, color: CL.bark, flexShrink: 0,
          }}>{g.name[0]}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: FONT.display, fontSize: 18, fontWeight: 500, color: CL.bark, lineHeight: 1.15 }}>{g.name}</span>
            <span style={{ display: 'block', fontSize: 12, color: CL.stone, marginTop: 1, fontFamily: FONT.body }}>{meta}</span>
          </span>
          <span style={{ color: CL.stone, flexShrink: 0, fontSize: 16, lineHeight: 1 }}>›</span>
        </button>
      );
    }

    function GroupHeader({ label, count }) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px 6px', justifyContent: 'space-between' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Diamond size={5} />
            <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontWeight: 700, fontFamily: FONT.body }}>{label}</span>
          </span>
          <span style={{ fontSize: 11, color: CL.stone, fontFamily: FONT.body }}>{count} {lang === 'bg' ? (count === 1 ? 'гост' : 'гости') : (count === 1 ? 'guest' : 'guests')}</span>
        </div>
      );
    }

    content = (
      <div>
        <TopBar onMenuClick={() => setDrawerOpen(true)} onLogoClick={() => { setTab('today'); setExpandedSquare(null); }} property={property} lang={lang} onTrialPillClick={() => setShowTrialModal(true)} />
        <div style={{ padding: '10px 14px 0' }}>
          <div style={{ position: 'relative' }}>
            <input style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 12px 10px 38px', height: 40,
              border: `1px solid ${CL.goldL}`, borderRadius: 999,
              fontSize: 14, fontFamily: FONT.body, background: CL.cream,
              color: CL.bark, outline: 'none',
            }} placeholder={t.searchPh} value={guestSearch} onChange={e => setGuestSearch(e.target.value)} />
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: CL.stone }}><Ic n="search" sz={14} /></span>
          </div>
        </div>

        {repeats.length > 0 && (
          <>
            <GroupHeader label={t.returning} count={repeats.length} />
            {repeats.map(g => <GuestRow key={g.phone} g={g} returning />)}
          </>
        )}

        {singles.length > 0 && (
          <>
            <GroupHeader label={lang === 'bg' ? 'ВСИЧКИ' : 'All'} count={singles.length} />
            {singles.map(g => <GuestRow key={g.phone} g={g} returning={false} />)}
          </>
        )}

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', color: CL.stone, padding: '40px 14px', fontSize: 13 }}>
            {guestSearch ? t.noMatch : t.noGYet}
          </div>
        )}
      </div>
    );
  }

  // ── ROOMS ──
  else if (tab === "rooms") {
    // Persist a new room_type_info object for the current property and refresh
    // local state. Returns true on success, false on failure (toast surfaces).
    // Shared by AddRoomModal and the RoomRow inline edit panel.
    async function updateRoomTypeInfo(nextInfo) {
      if (!canEdit) return;
      const { error } = await supabase.from('properties').update({ room_type_info: nextInfo }).eq('id', property.id);
      if (error) { showToast(lang === 'bg' ? 'Грешка при запазване на типа' : 'Error saving type'); return false; }
      setProperty(prev => ({ ...prev, room_type_info: nextInfo }));
      return true;
    }

    function renderSeasonEditor(seasons, setSeasons) {
      return (
        <div>
          {seasons.map((s, idx) => (
            <div key={s.id || idx} style={{ background: CL.sand, borderRadius: 12, padding: "12px", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <input style={{ ...st.inp, flex: 1, padding: "7px 10px", fontSize: 13, fontWeight: 600 }} value={s.label}
                  onChange={e => { const ns = [...seasons]; ns[idx] = { ...ns[idx], label: e.target.value }; setSeasons(ns); }} />
                <button onClick={() => setSeasons(seasons.filter((_, j) => j !== idx))}
                  style={{ background: "none", border: "none", color: CL.terra, cursor: "pointer", fontSize: 20, padding: "0 2px", lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <input style={{ ...st.inp, flex: "1 1 130px", minWidth: 0, padding: "6px 8px", fontSize: 13 }} type="date" value={new Date().getFullYear() + "-" + s.fromDate}
                  onChange={e => {
                    const newFrom = e.target.value.slice(5);
                    const ns = [...seasons];
                    ns[idx] = { ...ns[idx], fromDate: newFrom };
                    if (idx > 0) {
                      const parts = newFrom.split("-");
                      const yr = new Date().getFullYear();
                      const prevDay = new Date(yr, parseInt(parts[0]) - 1, parseInt(parts[1]) - 1);
                      ns[idx - 1] = { ...ns[idx - 1], toDate: String(prevDay.getMonth() + 1).padStart(2, "0") + "-" + String(prevDay.getDate()).padStart(2, "0") };
                    }
                    setSeasons(ns);
                  }} />
                <span style={{ fontSize: 14, color: CL.stone, flexShrink: 0 }}>→</span>
                <input style={{ ...st.inp, flex: "1 1 130px", minWidth: 0, padding: "6px 8px", fontSize: 13 }} type="date" value={new Date().getFullYear() + "-" + s.toDate}
                  onChange={e => {
                    const newTo = e.target.value.slice(5);
                    const ns = [...seasons];
                    ns[idx] = { ...ns[idx], toDate: newTo };
                    if (idx < ns.length - 1) {
                      const parts = newTo.split("-");
                      const yr = new Date().getFullYear();
                      const nextDay = new Date(yr, parseInt(parts[0]) - 1, parseInt(parts[1]) + 1);
                      ns[idx + 1] = { ...ns[idx + 1], fromDate: String(nextDay.getMonth() + 1).padStart(2, "0") + "-" + String(nextDay.getDate()).padStart(2, "0") };
                    }
                    setSeasons(ns);
                  }} />
                <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, color: CL.stone }}>{cur}</span>
                  <input style={{ ...st.inp, width: 68, padding: "6px 8px", fontSize: 13 }} type="number" min="0" value={s.price ?? ''}
                    onChange={e => { const ns = [...seasons]; ns[idx] = { ...ns[idx], price: e.target.value }; setSeasons(ns); }} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                  <span style={{ fontSize: 12, color: CL.stone }}>{t.minNights}</span>
                  <input style={{ ...st.inp, width: 48, padding: "6px 8px", fontSize: 13 }} type="number" min="1" value={s.min_stay ?? 1}
                    onChange={e => { const ns = [...seasons]; ns[idx] = { ...ns[idx], min_stay: Math.max(1, Number(e.target.value) || 1) }; setSeasons(ns); }} />
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => {
            const lastTo = seasons.length > 0 ? seasons[seasons.length - 1].toDate : "04-30";
            const parts = lastTo.split("-");
            const yr = new Date().getFullYear();
            const dt = new Date(yr, parseInt(parts[0]) - 1, parseInt(parts[1]) + 1);
            const newFrom = String(dt.getMonth() + 1).padStart(2, "0") + "-" + String(dt.getDate()).padStart(2, "0");
            setSeasons([...seasons, { id: uid(), fromDate: newFrom, toDate: "09-30", label: (lang === "bg" ? "Период " : "Period ") + (seasons.length + 1), price: 0 }]);
          }}
            style={{ background: "none", border: "1px dashed " + CL.gold, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: CL.gold, cursor: "pointer" }}>{t.addSeason}</button>
        </div>
      );
    }

    function applyBulk(type, seasons, capacity, maxAdults, maxChildren) {
      const isAptType = type === "apt";
      const updated = rooms.map(r => {
        if (r.archived) return r;
        if (isAptType ? isApt(r.id) : !isApt(r.id)) {
          const cleanSeasons = seasons.map(s => ({ ...s, price: Number(s.price) || 0 }));
          const basePrice = cleanSeasons.length > 0 ? cleanSeasons[0].price : r.basePrice;
          return { ...r, seasons: cleanSeasons, basePrice, capacity, maxAdults, maxChildren };
        }
        return r;
      });
      saveRooms(updated);
      setBulkEdit(null);
      showToast(t.bulkApplied);
    }

    const activeRooms = rooms.filter(r => !r.archived);
    const archivedRooms = rooms.filter(r => r.archived);
    const firstRoom = activeRooms.find(r => !isApt(r.id));
    const firstApt = activeRooms.find(r => isApt(r.id));
    const stdRooms = activeRooms.filter(r => !isApt(r.id));
    const aptRooms = activeRooms.filter(r => isApt(r.id));

    // "Rooms: 1–8 · Apartments: 9, 10" — collapse consecutive numeric names into
    // ranges, list any non-numeric names as-is.
    function summariseRoomNumbers(list) {
      const nums = [], others = [];
      list.forEach(r => {
        const raw = String(r.name).trim();
        const n = Number(raw);
        if (raw !== '' && Number.isInteger(n) && String(n) === raw) nums.push(n);
        else others.push(r.name);
      });
      nums.sort((a, b) => a - b);
      const parts = [];
      for (let i = 0; i < nums.length;) {
        let j = i;
        while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
        parts.push(i === j ? `${nums[i]}` : `${nums[i]}–${nums[j]}`);
        i = j + 1;
      }
      return [...parts, ...others].join(', ');
    }

    // Local atoms shared between the list and the editor screen.
    const lblSm = { display: 'block', fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, marginBottom: 6, fontFamily: FONT.body };
    const inpAt = { width: '100%', padding: '10px 12px', border: `1.5px solid ${CL.goldL}`, borderRadius: 9, fontSize: 14, fontFamily: FONT.body, background: '#FFFEF9', outline: 'none', color: CL.bark, height: 40, boxSizing: 'border-box' };

    function PricingCard({ tplType, tplRooms, label, gold }) {
      const sample = tplRooms[0];
      if (!sample) return null;
      const sleeps = sample.capacity ?? '—';
      const minStays = (sample.seasons || []).map(s => s.min_stay).filter(n => n > 1);
      const minStay = minStays.length > 0 ? Math.min(...minStays) : null;
      const accent = gold ? CL.gold : CL.goldL;
      return (
        <button onClick={() => setBulkEdit({
          type: tplType,
          seasons: (sample.seasons || []).map(s => ({ ...s })),
          capacity: sample.capacity ?? (gold ? 5 : 4),
          maxAdults: sample.maxAdults ?? null,
          maxChildren: sample.maxChildren ?? null,
        })} style={{
          width: 'calc(100% - 28px)', display: 'block',
          background: CL.cream, border: `1px solid ${accent}`, borderRadius: 14,
          margin: '8px 14px', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 16px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: FONT.display, fontSize: 20, fontWeight: 500, color: CL.bark, lineHeight: 1.15 }}>
                {label} <span style={{ fontFamily: FONT.body, fontSize: 12, color: CL.stone, fontWeight: 500, marginLeft: 4 }}>{tplRooms.length} {tplType === 'apt' ? (tplRooms.length === 1 ? (lang === 'bg' ? 'апартамент' : 'apartment') : (lang === 'bg' ? 'апартамента' : 'apartments')) : (tplRooms.length === 1 ? (lang === 'bg' ? 'стая' : 'room') : (lang === 'bg' ? 'стаи' : 'rooms'))}</span>
              </div>
              <div style={{ fontSize: 11.5, color: CL.stone, marginTop: 2, fontFamily: FONT.body }}>
                {lang === 'bg' ? `Капацитет ${sleeps}` : `Capacity ${sleeps}`}{minStay ? ` · ${lang === 'bg' ? `мин. престой ${minStay}н` : `min stay ${minStay}n`}` : ''}
              </div>
            </div>
            <span style={{ color: CL.stone, fontSize: 18, lineHeight: 1, flexShrink: 0 }}>›</span>
          </div>
          <div style={{ padding: '0 16px 14px' }}>
            {(sample.seasons || []).map((s, i) => {
              const [fm, fd] = s.fromDate.split('-');
              const [tm, td] = s.toDate.split('-');
              const monthNames = lang === 'bg' ? ['яну','фев','мар','апр','май','юни','юли','авг','сеп','окт','ное','дек'] : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              const sameMonth = fm === tm;
              const range = sameMonth
                ? `${monthNames[parseInt(fm) - 1]}`
                : `${monthNames[parseInt(fm) - 1]}–${monthNames[parseInt(tm) - 1]}`;
              const detail = sameMonth ? `${monthNames[parseInt(fm) - 1]} ${fd}–${td}` : `${monthNames[parseInt(fm) - 1]} ${fd} – ${monthNames[parseInt(tm) - 1]} ${td}`;
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0', borderTop: i > 0 ? `1px solid ${CL.goldL}` : 'none' }}>
                  <span style={{ fontSize: 12.5, color: CL.bark, fontFamily: FONT.body }}>
                    <span style={{ fontWeight: 600 }}>{s.label}</span>{' '}
                    <span style={{ color: CL.stone }}>{detail}</span>
                  </span>
                  <span style={{ fontFamily: FONT.display, fontSize: 16, fontWeight: 500, color: CL.bark, fontVariantNumeric: 'lining-nums tabular-nums' }}>{cur}{s.price}</span>
                </div>
              );
            })}
          </div>
        </button>
      );
    }

    function SectionHeaderRow({ label, count }) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 6px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Diamond size={5} />
            <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontWeight: 700, fontFamily: FONT.body }}>{label}</span>
          </span>
          {count != null && <span style={{ fontSize: 11, color: CL.stone, fontFamily: FONT.body }}>{count}</span>}
        </div>
      );
    }

    function RoomRow({ r }) {
      const isOcc = occIds.has(r.id);
      const apt = isApt(r.id);
      const currentRate = getRate(r, isoToday(new Date()));
      const isEditing = editingRoomId === r.id;
      const draft = isEditing ? roomDraft : null;
      const hasBookings = bks.some(b => b.roomId === r.id);
      const tileBg = apt ? CL.apt : CL.cream;
      const tileBorder = apt ? CL.gold : CL.goldL;
      function toggle() {
        if (isEditing) { setEditingRoomId(null); setRoomDraft(null); setEditCustomOpen(false); }
        else {
          setEditingRoomId(r.id);
          setRoomDraft({
            name: r.name,
            type: r.type,
            basePrice: r.basePrice,
            capacity: r.capacity,
            maxAdults: r.maxAdults,
            maxChildren: r.maxChildren,
            advancedOpen: r.maxAdults != null || r.maxChildren != null,
          });
        }
      }
      return (
        <div style={{
          background: CL.cream, border: `1px solid ${isEditing ? CL.gold : CL.goldL}`, borderRadius: 12,
          margin: '8px 14px', padding: 0, overflow: 'hidden',
        }}>
          <button onClick={toggle} style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            background: 'none', border: 'none', padding: '10px 14px',
            cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
          }}>
            <span style={{
              width: 38, height: 38, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${tileBorder}`, background: tileBg, color: CL.bark,
              fontFamily: FONT.body, fontSize: 18, fontWeight: 700, flexShrink: 0,
              fontVariantNumeric: 'lining-nums tabular-nums',
            }}>{r.name}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 12, color: CL.bark, fontFamily: FONT.body }}>
                <span style={{ fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', fontSize: 10, color: apt ? CL.gold : CL.stone }}>{apt ? t.aptLabel : t.roomLabel}</span>
                {' · '}
                <span style={{ color: CL.stone }}>{lang === 'bg' ? `Капацитет ${r.capacity}` : `Capacity ${r.capacity}`}</span>
              </span>
              {isEditing && (
                <span style={{ display: 'block', fontSize: 10.5, color: CL.stone, marginTop: 2, letterSpacing: 0.4 }}>
                  {lang === 'bg' ? 'Натисни за свиване' : 'Tap to collapse'}
                </span>
              )}
            </span>
            <span style={{ textAlign: 'right', flexShrink: 0 }}>
              <span style={{ display: 'block', fontFamily: FONT.display, fontSize: 16, fontWeight: 500, color: CL.bark, fontVariantNumeric: 'lining-nums tabular-nums' }}>{cur}{currentRate}</span>
              <span style={{ display: 'block', fontSize: 9.5, letterSpacing: 1.4, color: isOcc ? CL.olive : CL.stone, fontWeight: 700, marginTop: 2 }}>
                {isOcc ? (lang === 'bg' ? '● ЗАЕТА' : '● OCCUPIED') : (lang === 'bg' ? 'СВОБОДНА' : 'FREE')}
              </span>
            </span>
          </button>

          {isEditing && draft && (
            <div style={{ background: CL.sand, padding: '14px 16px 14px' }}>
              <div style={{ marginBottom: 12 }}>
                <label style={lblSm}>{lang === 'bg' ? 'Име' : 'Name'}</label>
                <input value={draft.name}
                  onChange={e => setRoomDraft(d => ({ ...d, name: e.target.value }))}
                  style={{ ...inpAt }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={lblSm}>{lang === 'bg' ? 'Тип' : 'Type'}</label>
                  <select value={draft.type}
                    onChange={e => {
                      if (e.target.value === '__custom__') { setEditCustomOpen(true); return; }
                      setRoomDraft(d => ({ ...d, type: e.target.value }));
                    }}
                    style={{ ...inpAt }}>
                    {getKnownTypeKeys(property).map(k => (
                      <option key={k} value={k}>{resolveTypeLabel(k, property?.room_type_info, lang)}</option>
                    ))}
                    <option value="__custom__">{lang === 'bg' ? '+ Добави нов тип…' : '+ Add custom type…'}</option>
                  </select>
                </div>
                <div>
                  <label style={lblSm}>{lang === 'bg' ? 'Цена/нощ' : 'Price/night'} ({cur})</label>
                  <input type="number" min="0" value={draft.basePrice ?? ''}
                    onChange={e => setRoomDraft(d => ({ ...d, basePrice: e.target.value }))}
                    style={{ ...inpAt }} />
                </div>
              </div>

              {editCustomOpen && (
                <div style={{ marginBottom: 12 }}>
                  <CustomTypeForm
                    property={property}
                    lang={lang}
                    onCancel={() => setEditCustomOpen(false)}
                    onSelected={(key) => {
                      setRoomDraft(d => ({ ...d, type: key }));
                      setEditCustomOpen(false);
                    }}
                    onUpdateRoomTypeInfo={updateRoomTypeInfo}
                  />
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={lblSm}>{lang === 'bg' ? 'Капацитет за тази стая' : 'Capacity for this room'}</span>
                <span style={{ fontSize: 10, color: CL.stone, fontStyle: 'italic', marginBottom: 6, marginLeft: 'auto' }}>
                  {lang === 'bg' ? `замества ${apt ? 'Апартаменти' : 'Стандарт'}` : `overrides ${apt ? 'Apartments' : 'Standard'} template`}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
                <input type="number" min="1" max="20" value={draft.capacity}
                  onChange={e => setRoomDraft(d => ({ ...d, capacity: Number(e.target.value) || 1 }))}
                  style={{
                    width: 80, padding: '8px 10px', border: `1.5px solid ${CL.goldL}`, borderRadius: 9,
                    fontFamily: FONT.display, fontSize: 28, fontWeight: 500, color: CL.bark,
                    background: CL.cream, outline: 'none', textAlign: 'center',
                    fontVariantNumeric: 'lining-nums tabular-nums',
                  }} />
                <span style={{ fontSize: 11.5, color: CL.stone, lineHeight: 1.45, flex: 1 }}>
                  {lang === 'bg'
                    ? 'Макс. гости. Това е максимумът. Резервациите могат да смесват възрастни и деца до тази бройка.'
                    : 'Max guests. This is the hard limit. Bookings can mix adults and children up to this number.'}
                </span>
              </div>

              <button onClick={() => setRoomDraft(d => ({ ...d, advancedOpen: !d.advancedOpen }))} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontFamily: FONT.body,
                marginTop: 4, marginBottom: draft.advancedOpen ? 8 : 12,
              }}>
                {draft.advancedOpen ? '▾' : '▸'} {lang === 'bg' ? 'РАЗШИРЕНИ: ЛИМИТ ЗА ВЪЗРАСТНИ / ДЕЦА' : 'ADVANCED — LIMIT BY ADULTS / CHILDREN'}
              </button>

              {draft.advancedOpen && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lblSm}>{lang === 'bg' ? 'Макс. възрастни' : 'Max adults'}</label>
                    <input type="number" min="1" max="20" value={draft.maxAdults ?? ''}
                      placeholder={lang === 'bg' ? 'без лимит' : 'no limit'}
                      onChange={e => setRoomDraft(d => ({ ...d, maxAdults: e.target.value === '' ? null : Number(e.target.value) }))}
                      style={{ ...inpAt, textAlign: 'center' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lblSm}>{lang === 'bg' ? 'Макс. деца' : 'Max children'}</label>
                    <input type="number" min="0" max="20" value={draft.maxChildren ?? ''}
                      placeholder={lang === 'bg' ? 'без лимит' : 'no limit'}
                      onChange={e => setRoomDraft(d => ({ ...d, maxChildren: e.target.value === '' ? null : Number(e.target.value) }))}
                      style={{ ...inpAt, textAlign: 'center' }} />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={() => setArchiveConfirm(r)} style={{
                  background: 'none', border: 'none', color: CL.stone, cursor: 'pointer',
                  fontSize: 12, padding: '8px 4px', textDecoration: 'underline', textUnderlineOffset: 3,
                  fontFamily: FONT.body,
                }}>{lang === 'bg' ? 'Архивирай' : 'Archive'}</button>

                <button onClick={async () => {
                  if (!canEdit) return;
                  // Next free room number across all rooms (active + archived).
                  const maxNum = rooms.reduce((m, x) => {
                    const raw = String(x.name).trim();
                    const n = Number(raw);
                    return (Number.isInteger(n) && String(n) === raw) ? Math.max(m, n) : m;
                  }, 0);
                  const srcRaw = String(r.name).trim();
                  const srcNum = Number(srcRaw);
                  const newName = (Number.isInteger(srcNum) && String(srcNum) === srcRaw)
                    ? String(maxNum + 1)
                    : `${r.name} ${lang === 'bg' ? 'копие' : 'copy'}`;
                  const maxSort = rooms.reduce((m, x) => Math.max(m, x.sortOrder ?? 0), 0);
                  const newRoom = {
                    id: uid(),
                    name: newName,
                    type: r.type,
                    capacity: r.capacity,
                    basePrice: r.basePrice,
                    seasons: (r.seasons || []).map(s => ({ ...s })),
                    maxAdults: r.maxAdults,
                    maxChildren: r.maxChildren,
                    sortOrder: maxSort + 1,
                    archived: false,
                  };
                  await saveRooms([...rooms, newRoom]);
                  showToast(lang === 'bg' ? `Стая ${newName} е добавена` : `Room ${newName} added`);
                }} style={{
                  background: 'none', border: 'none', color: CL.stone, cursor: 'pointer',
                  fontSize: 12, padding: '8px 4px', textDecoration: 'underline', textUnderlineOffset: 3,
                  fontFamily: FONT.body,
                }}>{lang === 'bg' ? 'Дублирай' : 'Duplicate'}</button>

                {!hasBookings && (
                  <button onClick={() => setDeleteConfirm(r)} style={{
                    background: 'none', border: 'none', color: CL.terra, cursor: 'pointer',
                    fontSize: 12, padding: '8px 4px', textDecoration: 'underline', textUnderlineOffset: 3,
                    fontFamily: FONT.body,
                  }}>{lang === 'bg' ? 'Изтрий' : 'Delete'}</button>
                )}

                <span style={{ flex: 1 }} />

                <button onClick={async () => {
                  const updated = rooms.map(x => x.id === r.id ? {
                    ...x,
                    name: (draft.name ?? x.name).trim() || x.name,
                    type: draft.type ?? x.type,
                    basePrice: (draft.basePrice === '' || draft.basePrice == null) ? x.basePrice : (Number(draft.basePrice) || 0),
                    capacity: draft.capacity,
                    maxAdults: draft.maxAdults,
                    maxChildren: draft.maxChildren,
                  } : x);
                  await saveRooms(updated);
                  setEditingRoomId(null);
                  setRoomDraft(null);
                  setEditCustomOpen(false);
                  showToast(lang === 'bg' ? 'Запазено!' : 'Saved!');
                }} style={{
                  background: CL.olive, color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 10,
                  fontFamily: FONT.body, fontSize: 13, fontWeight: 600, letterSpacing: 0.4, cursor: 'pointer',
                }}>{lang === 'bg' ? 'Запази' : 'Save'}</button>
                <button onClick={() => { setEditingRoomId(null); setRoomDraft(null); setEditCustomOpen(false); }} style={{
                  background: CL.cream, color: CL.bark, border: `1px solid ${CL.goldL}`, padding: '10px 18px', borderRadius: 10,
                  fontFamily: FONT.body, fontSize: 13, fontWeight: 600, letterSpacing: 0.4, cursor: 'pointer',
                }}>{t.cancel}</button>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (bulkEdit) {
      const tplLabel = bulkEdit.type === 'apt' ? (lang === 'bg' ? 'Апартаменти' : 'Apartments') : (lang === 'bg' ? 'Стандартни стаи' : 'Standard rooms');
      content = (
        <div>
          {/* Drill-down header — back ‹ + Cormorant title */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 12px', background: CL.cream, position: 'sticky', top: 0, zIndex: 50, borderBottom: `1px solid ${HAIRLINE}` }}>
            <button onClick={() => setBulkEdit(null)} aria-label="Back" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 22, lineHeight: 1, color: CL.bark, width: 22 }}>‹</button>
            <span style={{ fontFamily: FONT.display, fontSize: 17, fontWeight: 500, color: CL.bark }}>{tplLabel}</span>
            <span style={{ width: 22 }} />
          </div>

          {/* ◆ CAPACITY */}
          <div style={{ background: CL.cream, margin: '10px 14px', borderRadius: 14, border: `1px solid ${CL.goldL}`, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Diamond size={5} />
                <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontWeight: 700, fontFamily: FONT.body }}>{lang === 'bg' ? 'Капацитет' : 'Capacity'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
                <input type="number" min="1" max="20" value={bulkEdit.capacity}
                  onChange={e => setBulkEdit({ ...bulkEdit, capacity: Number(e.target.value) || 1 })}
                  style={{
                    width: 80, padding: '10px 12px', border: `1.5px solid ${CL.goldL}`, borderRadius: 9,
                    fontFamily: FONT.display, fontSize: 28, fontWeight: 500, color: CL.bark,
                    background: '#FFFEF9', outline: 'none', textAlign: 'center',
                    fontVariantNumeric: 'lining-nums tabular-nums',
                  }} />
                <span style={{ fontSize: 12, color: CL.stone, lineHeight: 1.45, flex: 1, paddingTop: 6 }}>
                  {lang === 'bg'
                    ? 'Макс. гости на стая. Това е максимумът. Резервациите могат да смесват възрастни и деца до тази бройка.'
                    : 'Max guests per room. This is the hard limit. Bookings can mix adults and children up to this number.'}
                </span>
              </div>

              <button onClick={() => setBulkEdit({ ...bulkEdit, advancedOpen: !bulkEdit.advancedOpen })} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontSize: 9.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontFamily: FONT.body,
              }}>
                {bulkEdit.advancedOpen ? '▾' : '▸'} {lang === 'bg' ? 'РАЗШИРЕНИ: ЛИМИТ ЗА ВЪЗРАСТНИ / ДЕЦА' : 'ADVANCED — LIMIT BY ADULTS / CHILDREN'}
              </button>

              {bulkEdit.advancedOpen && (
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lblSm}>{lang === 'bg' ? 'Макс. възрастни' : 'Max adults'}</label>
                    <input type="number" min="1" max="20" value={bulkEdit.maxAdults ?? ''}
                      placeholder={lang === 'bg' ? 'без лимит' : 'no limit'}
                      onChange={e => setBulkEdit({ ...bulkEdit, maxAdults: e.target.value === '' ? null : Number(e.target.value) })}
                      style={{ ...inpAt, textAlign: 'center' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lblSm}>{lang === 'bg' ? 'Макс. деца' : 'Max children'}</label>
                    <input type="number" min="0" max="20" value={bulkEdit.maxChildren ?? ''}
                      placeholder={lang === 'bg' ? 'без лимит' : 'no limit'}
                      onChange={e => setBulkEdit({ ...bulkEdit, maxChildren: e.target.value === '' ? null : Number(e.target.value) })}
                      style={{ ...inpAt, textAlign: 'center' }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ◆ RATE PERIODS */}
          <div style={{ background: CL.cream, margin: '10px 14px', borderRadius: 14, border: `1px solid ${CL.goldL}`, overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Diamond size={5} />
                <span style={{ fontSize: 10, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontWeight: 700, fontFamily: FONT.body }}>{lang === 'bg' ? 'Периоди на цени' : 'Rate periods'}</span>
              </div>
              {renderSeasonEditor(bulkEdit.seasons, (ns) => setBulkEdit({ ...bulkEdit, seasons: ns }))}
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 14px 16px', background: CL.sand, borderTop: `1px solid ${CL.goldL}`, display: 'flex', gap: 10, paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}>
            <button onClick={() => applyBulk(bulkEdit.type, bulkEdit.seasons, bulkEdit.capacity, bulkEdit.maxAdults, bulkEdit.maxChildren)} style={{
              flex: 1, background: CL.olive, color: '#fff', border: 'none', padding: 14, borderRadius: 11,
              fontFamily: FONT.body, fontWeight: 600, fontSize: 14, letterSpacing: 0.4, cursor: 'pointer',
            }}>{lang === 'bg' ? 'Запази промените' : 'Save changes'}</button>
            <button onClick={() => setBulkEdit(null)} style={{
              flex: 1, background: CL.cream, color: CL.bark, border: `1px solid ${CL.goldL}`, padding: 14, borderRadius: 11,
              fontFamily: FONT.body, fontWeight: 600, fontSize: 14, letterSpacing: 0.4, cursor: 'pointer',
            }}>{t.cancel}</button>
          </div>
        </div>
      );
    } else {
      content = (
        <div>
          <TopBar onMenuClick={() => setDrawerOpen(true)} onLogoClick={() => { setTab('today'); setExpandedSquare(null); }} property={property} lang={lang} onTrialPillClick={() => setShowTrialModal(true)} />

          <div style={{ padding: '14px 14px 4px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontFamily: FONT.display, fontSize: 18, fontWeight: 500, color: CL.bark }}>
                {lang === 'bg' ? 'Твоите стаи' : 'Your rooms'}
              </span>
              <button
                data-testid="add-room-btn"
                onClick={() => setAddRoomOpen(true)}
                style={{
                  background: 'none', border: '1px dashed ' + CL.gold, borderRadius: 8,
                  padding: '6px 12px', fontSize: 12, fontWeight: 600,
                  color: CL.gold, cursor: 'pointer', fontFamily: FONT.body,
                }}
              >
                {lang === 'bg' ? '+ Добави стая' : '+ Add room'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: CL.stone, marginBottom: 4 }}>
              {lang === 'bg'
                ? 'Натиснете стая, за да я редактирате или архивирате.'
                : 'Tap to edit or archive a room.'}
            </div>
            {(stdRooms.length > 0 || aptRooms.length > 0) && (
              <div style={{ fontSize: 11.5, color: CL.stone, marginTop: 2, fontFamily: FONT.body }}>
                {stdRooms.length > 0 && (
                  <span><span style={{ fontWeight: 700, color: CL.bark }}>{lang === 'bg' ? 'Стаи' : 'Rooms'}:</span> {summariseRoomNumbers(stdRooms)}</span>
                )}
                {stdRooms.length > 0 && aptRooms.length > 0 && <span style={{ color: CL.goldL }}>{'  ·  '}</span>}
                {aptRooms.length > 0 && (
                  <span><span style={{ fontWeight: 700, color: CL.bark }}>{lang === 'bg' ? 'Апартаменти' : 'Apartments'}:</span> {summariseRoomNumbers(aptRooms)}</span>
                )}
              </div>
            )}
          </div>

          <SectionHeaderRow label={lang === 'bg' ? 'Цени' : 'Pricing'} count={null} />
          {stdRooms.length > 0 && <PricingCard tplType="room" tplRooms={stdRooms} label={lang === 'bg' ? 'Стандартни стаи' : 'Standard rooms'} gold={false} />}
          {aptRooms.length > 0 && <PricingCard tplType="apt" tplRooms={aptRooms} label={lang === 'bg' ? 'Апартаменти' : 'Apartments'} gold={true} />}

          <SectionHeaderRow label={lang === 'bg' ? 'Стаи' : 'Rooms'} count={`${activeRooms.length} ${lang === 'bg' ? 'общо' : 'total'}`} />
          {activeRooms.map(r => <RoomRow key={r.id} r={r} />)}

          {addRoomOpen && (
            <AddRoomModal
              property={property}
              lang={lang}
              canEdit={canEdit}
              onCancel={() => setAddRoomOpen(false)}
              onUpdateRoomTypeInfo={updateRoomTypeInfo}
              onSubmit={async (draft) => {
                const maxSort = rooms.reduce((m, r) => Math.max(m, r.sortOrder ?? 0), 0);
                const newRoom = {
                  id: uid(),
                  name: draft.name,
                  type: draft.type,
                  capacity: draft.capacity,
                  basePrice: draft.basePrice,
                  seasons: [],
                  maxAdults: draft.maxAdults,
                  maxChildren: draft.maxChildren,
                  sortOrder: maxSort + 1,
                  archived: false,
                };
                await saveRooms([...rooms, newRoom]);
                setAddRoomOpen(false);
                showToast(lang === 'bg' ? 'Стаята е добавена' : 'Room added');
              }}
            />
          )}

          {archiveConfirm && (
            <ConfirmModal
              title={lang === 'bg' ? `Архивиране на стая ${archiveConfirm.name}?` : `Archive room ${archiveConfirm.name}?`}
              body={lang === 'bg'
                ? 'Стаята ще се скрие от активния списък, но резервациите остават непокътнати. Не може да се възстанови от приложението.'
                : "It will hide from the active list but its bookings stay intact. You can't restore it from inside the app."}
              cancelLabel={lang === 'bg' ? 'Отказ' : 'Cancel'}
              confirmLabel={lang === 'bg' ? 'Архивирай' : 'Archive'}
              onCancel={() => setArchiveConfirm(null)}
              onConfirm={async () => {
                const updated = rooms.map(x => x.id === archiveConfirm.id ? { ...x, archived: true } : x);
                await saveRooms(updated);
                setArchiveConfirm(null);
                setEditingRoomId(null);
                setRoomDraft(null);
                setEditCustomOpen(false);
                showToast(lang === 'bg' ? 'Стаята е архивирана' : 'Room archived');
              }}
            />
          )}

          {deleteConfirm && (
            <DeleteRoomModal
              room={deleteConfirm}
              lang={lang}
              onCancel={() => setDeleteConfirm(null)}
              onConfirm={async () => {
                const id = deleteConfirm.id;
                // Hard delete — FK protected; if any booking still references this room,
                // Postgres will refuse the delete.
                const { error } = await supabase.from('rooms').delete().eq('id', id);
                if (error) {
                  showToast(lang === 'bg' ? 'Грешка при изтриване. Възможно е да има резервации. Архивирайте вместо това.' : "Couldn't delete — the room may have bookings. Archive instead.");
                  setDeleteConfirm(null);
                  return;
                }
                // Update local state
                const remaining = rooms.filter(x => x.id !== id);
                setRooms(remaining);

                // GC unused custom types from property.room_type_info
                const usedTypes = new Set(remaining.map(x => x.type));
                const info = property.room_type_info || {};
                const baselineKeys = new Set(Object.keys(BASELINE_TYPES_EN));
                const cleanedInfo = Object.fromEntries(
                  Object.entries(info).filter(([k]) => baselineKeys.has(k) || usedTypes.has(k)),
                );
                if (Object.keys(cleanedInfo).length !== Object.keys(info).length) {
                  const { error: gcErr } = await supabase.from('properties').update({ room_type_info: cleanedInfo }).eq('id', property.id);
                  if (gcErr) Sentry.captureException(gcErr);
                  else setProperty(p => ({ ...p, room_type_info: cleanedInfo }));
                }

                setDeleteConfirm(null);
                setEditingRoomId(null);
                setRoomDraft(null);
                setEditCustomOpen(false);
                showToast(lang === 'bg' ? 'Стаята е изтрита' : 'Room deleted');
              }}
            />
          )}

          {archivedRooms.length > 0 && (
            <div style={{ marginTop: 24, padding: '12px 14px', borderTop: `1px solid ${CL.goldL}` }}>
              <button onClick={() => setArchivedOpen(o => !o)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontSize: 11, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', color: CL.stone, fontFamily: FONT.body,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span>{archivedOpen ? '▾' : '▸'}</span>
                <span>{lang === 'bg' ? 'Архивирани стаи' : 'Archived rooms'}</span>
                <span style={{ background: CL.sand, color: CL.stone, padding: '1px 7px', borderRadius: 999, fontSize: 10, marginLeft: 4 }}>{archivedRooms.length}</span>
              </button>

              {archivedOpen && (
                <div style={{ marginTop: 10 }}>
                  {archivedRooms.map(r => (
                    <div key={r.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '8px 12px', marginTop: 6,
                      background: CL.sand, borderRadius: 8, opacity: 0.8,
                    }}>
                      <span style={{ fontFamily: FONT.display, fontSize: 14, color: CL.bark, flex: 1 }}>{r.name}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.4, color: CL.stone, textTransform: 'uppercase' }}>
                        ○ {resolveTypeLabel(r.type, property?.room_type_info, lang)}
                      </span>
                    </div>
                  ))}
                  <div style={{ marginTop: 10, fontSize: 11, color: CL.stone }}>
                    {lang === 'bg'
                      ? 'За да възстановите стая, пишете на hello@tavelo.app.'
                      : 'To restore an archived room, email hello@tavelo.app.'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
  }

  // ── SETTINGS ──
  else if (tab === "settings") {
    const bookingBase = (import.meta.env.VITE_BOOKING_BASE_URL || 'https://book.tavelo.app').trim();
    const directUrl = property?.slug ? `${bookingBase}/${property.slug.trim()}` : '';

    async function saveSettings(updates) {
      if (!canEdit) return;
      const { error } = await supabase.from('properties').update(updates).eq('id', property.id);
      if (error) { showToast('Error saving settings'); return; }
      setProperty(prev => ({ ...prev, ...updates }));
      showToast(lang === 'bg' ? 'Запазено!' : 'Saved!');
    }

    content = <SettingsPanel
      property={property}
      lang={lang}
      directUrl={directUrl}
      onSave={saveSettings}
      showToast={showToast}
      onBack={() => setTab("today")}
      rooms={rooms}
    />;
  }

  // ── ANALYTICS ──
  else if (tab === "analytics") {
    content = <Analytics
      property={property}
      rooms={rooms}
      t={t}
      lang={lang}
      canEdit={canEdit}
      onBack={() => { setTab("today"); setExpandedSquare(null); }}
      onMenuClick={() => setDrawerOpen(true)}
      onPropertyUpdate={(patch) => setProperty(p => ({ ...p, ...patch }))}
    />;
  }

  // ════════ RENDER ════════
  const showNav = !formInit && !viewBk && !viewGuest && !moveTarget;

  return (
    <div style={{ fontFamily: FONT.body, fontVariantNumeric: "lining-nums tabular-nums", background: CL.sand, minHeight: "100vh", width: "100%" }}>
      <div style={{ maxWidth: scr.maxW, margin: "0 auto", minHeight: "100vh", background: CL.sand, paddingBottom: 0, color: CL.bark, position: "relative" }}>
      {toast && (
        <div style={{ ...st.toast, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{toast.message}</span>
          {toast.undo && (
            <button onClick={() => { toast.undo(); setToast(null); }}
              style={{ background: 'none', border: 'none', color: CL.gold, fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: 0 }}>
              {lang === 'bg' ? 'Отмени' : 'Undo'}
            </button>
          )}
        </div>
      )}
      {!canEdit && !bannerDismissed && (
        <div data-testid="read-only-banner" style={{ background: '#FFFDF8', borderLeft: '4px solid #C0562B',
                      padding: '12px 16px', display: 'flex',
                      justifyContent: 'space-between', alignItems: 'center',
                      margin: '12px 0' }}>
          <span>
            {lang === 'bg'
              ? <>Режим само за преглед. Пишете ни на <a href="mailto:hello@tavelo.app">hello@tavelo.app</a>, за да продължите.</>
              : <>Read-only mode. Email <a href="mailto:hello@tavelo.app">hello@tavelo.app</a> to continue.</>}
          </span>
          <button onClick={() => setBannerDismissed(true)}
            style={{ background:'transparent', border:'none', cursor:'pointer', fontSize: 18 }}>×</button>
        </div>
      )}
      {content}
      {showTrialModal && <TrialModal property={property} lang={lang} onClose={() => setShowTrialModal(false)} />}
      {showNav && (tab === "today" || tab === "occupancy" || tab === "directory") && (
        <Fab onClick={startNew} />
      )}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        currentTab={tab}
        onSelectTab={(k) => { setTab(k); setDrawerOpen(false); }}
        property={property}
        lang={lang}
        toggleLang={toggleLang}
        onSignOut={() => supabase.auth.signOut()}
        hasAnalytics={property?.analytics_enabled !== false}
      />
      </div>
    </div>
  );
}
